(() => {
  'use strict';

  // Archive Shared Pour - read-only product-page presentation (Phase 3C).
  //
  // ONE page-level controller: it finds the Shared Pour root (blocks/archive-shared-pour.liquid,
  // directly under the purchase controls), makes ONE request to the app-proxy endpoint, and renders
  // the component from that response. Anything other than a valid 200 with a visible event leaves
  // the root hidden and empty. The backend decides visibility, state, progress, positions and
  // actions; this file only presents them. Nothing here writes: no commitment, reserve, payment or
  // decline requests.

  const TIMEOUT_MS = 8000;

  // The lifecycle states this version has wording and customer actions for. A state outside this
  // list is still shown (a later backend may add one), but read-only: package progress only, never
  // an action. Lifecycle status controls what a customer may do; progress shows what has happened.
  const PUBLIC_STATES = ['Upcoming', 'Open', 'Finalising', 'CollectingPayment', 'FullyFunded', 'Preparing', 'Completed', 'Closed', 'Cancelled'];
  const STATE_NAME = /^[A-Za-z][A-Za-z0-9]{0,39}$/;
  const SELECTING_STATES = ['Upcoming', 'Open'];
  // After the lock the bottle is reserved in Shopify for its Shared Pour (one unit held).
  const RESERVED_STATES = ['CollectingPayment', 'FullyFunded', 'Preparing'];
  // Recognised states in which visitors without commitments see nothing at all.
  const HIDDEN_FOR_VISITORS = ['Closed', 'Cancelled'];

  /**
   * The compact banner for visitors without commitments (guests where the backend shows the event,
   * and signed-in customers with none). No package, progress, position or customer detail: only
   * that the bottle is being shared. No count is shown until the backend provides a reliable
   * distinct-customer count. The purchase line is text only - Shopify's own Add to Cart and Shop
   * Pay are the purchase actions - and appears only while the bottle is not yet reserved
   * (Finalising) and Shopify reports it available (data-bottle-available, printed by the block).
   */
  const BANNER_COPY = {
    pending: {
      badge: 'Allocation pending',
      headline: 'A Rare Bottle. Already in Demand.',
      // Only beside the purchase line: it would promise too much when the bottle cannot be bought.
      tagline: 'You’re still not too late!',
      lines: ['Whisky lovers have already made tentative commitments to share this bottle.'],
      membersLine: 'have already made tentative commitments to share this bottle.',
      memberLine: 'has already made a tentative commitment to share this bottle.',
      openLine: 'The sharing arrangement is being finalised, but the opportunity to own the entire bottle is still open.',
      closedLine: 'The sharing arrangement is being finalised.'
    },
    reserved: {
      badge: 'Reserved for Shared Pour',
      headline: 'This Bottle Is Being Shared.',
      lines: ['This bottle is now reserved for its Shared Pour participants.']
    },
    completed: {
      badge: 'Shared',
      headline: 'Successfully Shared Through The Whisky Archive',
      lines: ['This bottle brought whisky lovers together through our Shared Pour experience.']
    }
  };
  const BANNER_INVITE = { title: 'Prefer it all to yourself?', text: 'Secure the entire bottle while you still can using Add to Cart above.' };
  // Guests only: Shopify's own sign-in (data-login-url returns to this product page). It promises
  // nothing the backend has not authorised and never suggests commitments reopen.
  const BANNER_SIGN_IN = { title: 'Already part of the Shared Pour?', text: 'Sign in to view your commitments and allocation status.', label: 'Sign in' };

  /**
   * The number of distinct Archive members sharing the bottle - only if the backend sends it.
   * PROPOSED field event.interest.members (not yet provided by the API): customers holding at least
   * one primary position in the winning option while Finalising, each counted once. Never derived
   * here and never invented: anything other than a whole number of at least 1 means "no count",
   * and the banner keeps its general wording.
   */
  function memberCount(event) {
    const members = isObject(event.interest) ? event.interest.members : null;
    return Number.isInteger(members) && members >= 1 && members <= 100000 ? members : null;
  }

  const STATE_LABELS = {
    Upcoming: 'Opening soon',
    Open: 'Open for commitments',
    Finalising: 'A pour option has filled',
    CollectingPayment: 'Collecting payment',
    FullyFunded: 'Fully funded',
    Preparing: 'Being prepared',
    Completed: 'Complete',
    Closed: 'Closed',
    Cancelled: 'Closed'
  };

  const FULFILMENT_LABELS = {
    AwaitingOpening: 'Awaiting opening',
    Decanting: 'Being decanted',
    Labelled: 'Labelled',
    Shipped: 'Shipped',
    Delivered: 'Delivered'
  };

  const HOW_IT_WORKS = [
    ['Choose your pour', 'Each option divides the same bottle into equal portions. You can commit to more than one option.'],
    ['Commit', 'Committing is free and sets your place in the queue. No payment is taken.'],
    ['One option fills first', 'The first option to reach its portions is selected. Commitments to the other options are released at no charge.'],
    ['The bottle is secured', 'Until a Shared Pour secures it, the bottle may still sell through other Archive channels. If it does, the Shared Pour closes and nobody is charged.'],
    ['Allocation and payment', 'Portions are allocated in commitment order, and later commitments wait in the reserve queue. Allocated portions are paid through secure Shopify checkout.'],
    ['Sealed until fully funded', 'The bottle stays sealed until every portion is paid.'],
    ['Decant and deliver', 'The bottle is opened and decanted under Archive supervision, then sent to you.']
  ];

  // Shown after step 7, after "Please note:". Educational only. Approved wording - do not rephrase.
  const HOW_IT_WORKS_NOTE = 'Shared Pour portions are available on a first-come, first-served basis. Once an option is fully committed, you may no longer be able to join that Shared Pour. However, you can still explore other eligible bottles and commit to portions where opportunities remain open.';

  // The compact product-page introduction: permanent, concept-level copy shown above the
  // state-specific lede. Approved wording - do not rephrase. ("Archive Shared Pour" stays the name
  // used in the How it works dialog, emails and information pages.)
  const INTRO = {
    heading: 'Shared Pour',
    tagline: 'Share a rare bottle, one pour at a time.',
    explanation: "Commit to a pour and we'll open the bottle once it's fully funded.",
    how: 'How Shared Pour works',
    // The approved Shared Pour symbol (golden Glencairn toast), served from the Shopify CDN.
    // Square artwork; decorative (the tagline beside it carries the meaning).
    iconUrl: 'https://cdn.shopify.com/s/files/1/0743/6582/9294/files/Golden_Glencairn_Toast.svg?v=1791350587'
  };

  const DISCLOSURE = 'A commitment is not a purchase and does not reserve the bottle. If the bottle sells through another Archive channel first, the Shared Pour closes and nobody is charged.';

  // ===== Text helpers =====================================================================

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function plural(count, one, many) {
    return `${count} ${count === 1 ? one : (many || `${one}s`)}`;
  }

  function formatMoney(money) {
    if (!money || !Number.isFinite(money.cents)) return '';
    const currency = typeof money.currency === 'string' && money.currency ? money.currency : 'AUD';
    try {
      return new Intl.NumberFormat('en-AU', { style: 'currency', currency, minimumFractionDigits: 2 }).format(money.cents / 100);
    } catch (error) {
      return `$${(money.cents / 100).toFixed(2)}`;
    }
  }

  function formatDateTime(iso, timeZone) {
    if (typeof iso !== 'string') return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const options = { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' };
    if (timeZone) options.timeZone = timeZone;
    return new Intl.DateTimeFormat('en-AU', options).format(date);
  }

  function validRanges(ranges) {
    return Array.isArray(ranges)
      ? ranges.filter((range) => range && Number.isFinite(range.from) && Number.isFinite(range.to))
      : [];
  }

  const rangeLabel = (range) => (range.from === range.to ? `#${range.from}` : `#${range.from}–#${range.to}`);

  function formatRanges(ranges) {
    return validRanges(ranges).map(rangeLabel).join(', ');
  }

  /**
   * The server's position ranges as natural English: "#1–#2", "#1–#2 and #6–#7",
   * "#1–#2, #6–#7 and #10". Each range is shown exactly as returned: separate FIFO lots with other
   * customers' portions between them are never merged into one first-to-last span.
   */
  function rangeList(ranges) {
    const labels = validRanges(ranges).map(rangeLabel);
    return labels.length <= 1 ? labels.join('') : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
  }

  function positionCount(ranges) {
    return validRanges(ranges).reduce((sum, range) => sum + Math.max(1, range.to - range.from + 1), 0);
  }

  /** "Position #10", "Positions #1–#2", "Positions #1–#2, #6–#7 and #10" ('' when there are none). */
  function positionsPhrase(ranges, noun = 'Position') {
    const count = positionCount(ranges);
    return count ? `${count === 1 ? noun : `${noun}s`} ${rangeList(ranges)}` : '';
  }

  /**
   * The customer's own reserve standing, from the server's reserve view. PortionsAheadOfYou counts
   * the reserve portions ahead of their FIRST reserve portion, so with more than one reserve range it
   * is said to be "ahead of your first", never implied for every portion.
   */
  function reserveParts(reserve) {
    if (!isObject(reserve) || !(reserve.portions > 0)) return null;
    const ranges = validRanges(reserve.ranges);
    const ahead = plural(reserve.portionsAheadOfYou || 0, 'portion');
    return {
      portions: plural(reserve.portions, 'portion'),
      positions: positionsPhrase(ranges, 'Reserve position'),
      ahead: ranges.length > 1 ? `${ahead} ahead of your first` : `${ahead} ahead of you`
    };
  }

  function paragraphs(text) {
    if (typeof text !== 'string' || !text.trim()) return [];
    return text.split(/\n\s*\n/).map((part) => part.trim()).filter(Boolean);
  }

  /** Only same-site relative paths and https URLs are used as links or images. */
  function safeUrl(value) {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (/^\/(?!\/)/.test(trimmed)) return trimmed;
    if (/^\/\//.test(trimmed)) return `https:${trimmed}`;
    if (/^https:\/\//i.test(trimmed)) return trimmed;
    return null;
  }

  /** Shopify CDN images can be resized with ?width=; anything else is used as-is. */
  function imageSources(url) {
    const safe = safeUrl(url);
    if (!safe) return null;
    const shopifyCdn = /^https:\/\/cdn\.shopify\.com\//i.test(safe) || /^(https:\/\/[^/]+)?\/cdn\/shop\//i.test(safe);
    if (!shopifyCdn || typeof URL !== 'function') {
      return { src: safe, srcset: '' };
    }
    const relative = safe.startsWith('/');
    const sized = (width) => {
      const parsed = new URL(safe, 'https://relative.invalid');
      parsed.searchParams.set('width', String(width));
      return relative ? `${parsed.pathname}${parsed.search}` : parsed.toString();
    };
    return { src: sized(720), srcset: [360, 540, 720, 960].map((w) => `${sized(w)} ${w}w`).join(', ') };
  }

  // ===== Response validation ================================================================

  const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
  const isCount = (value) => Number.isInteger(value) && value >= 0;
  const isMoney = (value) => isObject(value) && Number.isFinite(value.cents);

  // A package's structure. Its progress is judged on its own (progressOf): a package whose progress
  // is missing or inconsistent still shows, as "Progress unavailable".
  function isValidPackage(pkg) {
    return isObject(pkg) &&
      typeof pkg.packageId === 'string' && pkg.packageId !== '' &&
      typeof pkg.name === 'string' && pkg.name !== '' &&
      isCount(pkg.portionMl) && pkg.portionMl > 0 &&
      isMoney(pkg.pricePerPortion) &&
      typeof pkg.packageState === 'string';
  }

  /**
   * The server's commitment progress for a package, or null when it is missing or inconsistent:
   * committed above the target, counts that do not add up, or a percentage that disagrees with
   * them. Null is drawn as "Progress unavailable" - never as a misleading bar.
   */
  function progressOf(progress) {
    if (!isObject(progress)) return null;
    const target = progress.primaryTarget;
    const committed = progress.primaryCommitted;
    const remaining = progress.primaryRemaining;
    if (!isCount(target) || target <= 0 || !isCount(committed) || !isCount(remaining)) return null;
    if (committed > target || committed + remaining !== target) return null;
    const percent = Math.floor((committed * 100) / target);
    if (progress.percentFilled !== undefined && progress.percentFilled !== null &&
      (!Number.isFinite(progress.percentFilled) || Math.abs(progress.percentFilled - percent) > 1)) return null;
    return { target, committed, remaining, percent };
  }

  /**
   * The minimum shape the presentation needs; anything malformed is "nothing to show", so an
   * unexpected response can never half-render. The lifecycle state only has to be a plain,
   * non-empty name: a state this version does not know is shown read-only (buildViewModel), and
   * hiding an event stays the backend's decision (404 / release gate).
   */
  function isValidResponse(response) {
    if (!isObject(response) || !isObject(response.event)) return false;
    const event = response.event;
    return typeof event.sharedPourId === 'string' && event.sharedPourId !== '' &&
      typeof event.publicState === 'string' && STATE_NAME.test(event.publicState) &&
      Array.isArray(event.packages) && event.packages.length > 0 &&
      event.packages.every(isValidPackage) &&
      (event.me === null || event.me === undefined || isObject(event.me)) &&
      (event.stage === undefined || event.stage === null || isObject(event.stage));
  }

  // ===== Loading ===========================================================================

  /**
   * One GET. Returns the response only for a 200 with a valid, visible event; otherwise null.
   * options.outcome (an object), when given, receives kind: 'shown' (a valid event), 'none' (the
   * backend confirms nothing for this visitor: 404, 401, 403 or no event) or 'failed' (a server
   * error, timeout, network failure or unrecognised answer - nothing is concluded).
   */
  async function loadStorefront(endpoint, options = {}) {
    const outcome = isObject(options.outcome) ? options.outcome : {};
    outcome.kind = 'failed';
    const fetchImpl = options.fetchImpl || (typeof fetch === 'function' ? fetch.bind(globalThis) : null);
    const timeoutMs = options.timeoutMs || TIMEOUT_MS;
    if (!fetchImpl || typeof endpoint !== 'string' || !endpoint) return null;

    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    try {
      const response = await fetchImpl(endpoint, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal: controller ? controller.signal : undefined
      });
      if (!response || response.status !== 200) {
        debug(`no Shared Pour (HTTP ${response ? response.status : 'none'})`);
        if (response && NO_EVENT_STATUSES.includes(response.status)) outcome.kind = 'none';
        return null;
      }
      const json = await response.json();
      if (isObject(json) && json.event === null) {
        debug('no Shared Pour (no event)');
        outcome.kind = 'none';
        return null;
      }
      if (!isValidResponse(json)) {
        debug('no Shared Pour (response not recognised)');
        return null;
      }
      outcome.kind = 'shown';
      return json;
    } catch (error) {
      debug(`no Shared Pour (${error && error.name === 'AbortError' ? 'timeout' : 'request failed'})`);
      return null;
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  // ===== Loader hint ===========================================================================
  //
  // Decides ONLY whether the block's inline script draws the Archive loader before the first answer
  // (blocks/archive-shared-pour.liquid). It grants nothing and reveals nothing: the request and the
  // backend's decision are exactly the same with or without it. Stored per browser as
  //   { v: 2, scopes: { <scope>: { <productId>: { e: <expiresAtMs>, c: 1 | 0 } } } }
  // (c: the visitor last saw the compact banner, so the inline script draws the compact loader).
  // where the scope is the block's data-hint-scope (a one-way hash of the signed-in customer, or
  // "guest") and the value is only a product id and a date. Written after a confirmed panel;
  // removed when the backend confirms nothing for this visitor; never touched by a failed read.
  // Unavailable storage (private mode, blocked) simply means no hint.

  const HINT_KEY = 'twaArchiveSharedPourHint.v1';
  const HINT_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  const HINT_LIMIT = 40;
  const HINT_SCOPE = /^(guest|[a-f0-9]{16,64})$/;
  const HINT_PRODUCT = /^\d{1,20}$/;
  const NO_EVENT_STATUSES = [401, 403, 404];

  function hintTarget(root, win) {
    const scope = root && typeof root.getAttribute === 'function' ? root.getAttribute('data-hint-scope') : null;
    const productId = root && typeof root.getAttribute === 'function' ? root.getAttribute('data-product-id') : null;
    if (!scope || !productId || !HINT_SCOPE.test(scope) || !HINT_PRODUCT.test(productId)) return null;
    let storage = null;
    try { storage = win && win.localStorage ? win.localStorage : null; } catch (error) { storage = null; }
    return storage ? { storage, scope, productId } : null;
  }

  function updateHint(target, present, now = Date.now(), compact = false) {
    if (!target) return;
    try {
      let data = null;
      try { data = JSON.parse(target.storage.getItem(HINT_KEY) || 'null'); } catch (error) { data = null; }
      if (!isObject(data) || data.v !== 2 || !isObject(data.scopes)) data = { v: 2, scopes: {} };
      const expiry = (entry) => (isObject(entry) && Number.isFinite(entry.e) ? entry.e : NaN);
      // Expired entries go, and any scope left empty.
      for (const scope of Object.keys(data.scopes)) {
        const entries = isObject(data.scopes[scope]) ? data.scopes[scope] : {};
        for (const id of Object.keys(entries)) if (!(expiry(entries[id]) > now)) delete entries[id];
        if (Object.keys(entries).length) data.scopes[scope] = entries; else delete data.scopes[scope];
      }
      const entries = isObject(data.scopes[target.scope]) ? data.scopes[target.scope] : {};
      if (present) {
        entries[target.productId] = { e: now + HINT_TTL_MS, c: compact ? 1 : 0 };
        // Bounded: the products seen longest ago drop out first.
        Object.keys(entries).sort((a, b) => expiry(entries[a]) - expiry(entries[b])).slice(0, Math.max(0, Object.keys(entries).length - HINT_LIMIT))
          .forEach((id) => { delete entries[id]; });
      } else {
        delete entries[target.productId];
      }
      if (Object.keys(entries).length) data.scopes[target.scope] = entries; else delete data.scopes[target.scope];
      if (Object.keys(data.scopes).length) target.storage.setItem(HINT_KEY, JSON.stringify(data));
      else target.storage.removeItem(HINT_KEY);
    } catch (error) {
      // Storage full or blocked: the hint is a nicety, never required.
    }
  }

  // ===== Guest preview link ====================================================================

  const PREVIEW_PARAMETER = 'sp_preview';
  // Shape check only (the backend issues 43-character base64url tokens). It grants nothing: the
  // backend alone decides whether the token shows anything.
  const PREVIEW_TOKEN_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;

  /**
   * Takes a guest preview token from the URL fragment (#sp_preview=...) and removes it from the
   * address bar straight away, keeping any other fragment content. A fragment never reaches a
   * server or a Referer header. The token is returned for in-memory use only: never stored.
   * Returns null when there is no well-formed token.
   */
  function takePreviewToken(win) {
    let hash = '';
    try { hash = (win && win.location && win.location.hash) || ''; } catch (error) { return null; }
    if (!hash || hash.indexOf(PREVIEW_PARAMETER) === -1) return null;

    // Split by hand so any other fragment content (e.g. #reviews) is kept exactly as it was.
    const parts = hash.replace(/^#/, '').split('&');
    const isPreview = (part) => part === PREVIEW_PARAMETER || part.indexOf(`${PREVIEW_PARAMETER}=`) === 0;
    const previewPart = parts.find(isPreview);
    if (previewPart === undefined) return null;
    let token = '';
    try { token = decodeURIComponent(previewPart.slice(PREVIEW_PARAMETER.length + 1)).trim(); } catch (error) { token = ''; }

    // Removed even when malformed, so a stray token never lingers in the address bar.
    try {
      const rest = parts.filter((part) => !isPreview(part)).join('&');
      const { pathname, search } = win.location;
      win.history.replaceState(win.history.state, '', `${pathname}${search}${rest ? `#${rest}` : ''}`);
    } catch (error) {
      debug('preview link could not be removed from the address bar');
    }
    return PREVIEW_TOKEN_PATTERN.test(token) ? token : null;
  }

  /** The product read URL. The preview token goes here and nowhere else. */
  function productEndpoint(config) {
    const token = config.previewToken;
    if (!token) return config.endpoint;
    return `${config.endpoint}${config.endpoint.indexOf('?') === -1 ? '?' : '&'}${PREVIEW_PARAMETER}=${encodeURIComponent(token)}`;
  }

  function debug(message) {
    if (typeof console !== 'undefined' && typeof console.debug === 'function') {
      console.debug(`[Archive Shared Pour] ${message}`);
    }
  }

  // ===== View model (presentation only) =====================================================

  function packageView(pkg, config, mine) {
    const progress = progressOf(pkg.progress);
    const image = imageSources((pkg.image && pkg.image.url) || config.fallbackImageUrl);
    const thumb = imageSources((pkg.thumbnailImage && pkg.thumbnailImage.url) || (pkg.image && pkg.image.url) || config.fallbackImageUrl);
    return {
      id: pkg.packageId,
      name: pkg.name,
      structure: progress ? `${progress.target} × ${pkg.portionMl} ml` : `${pkg.portionMl} ml portions`,
      priceText: formatMoney(pkg.pricePerPortion),
      image,
      thumb,
      progressAvailable: Boolean(progress),
      committed: progress ? progress.committed : null,
      target: progress ? progress.target : null,
      remaining: progress ? progress.remaining : null,
      percent: progress ? progress.percent : null,
      committedText: progress ? `${progress.committed} of ${plural(progress.target, 'portion')} committed` : '',
      remainingText: progress ? (progress.remaining === 0 ? 'Fully committed' : `${plural(progress.remaining, 'portion')} remaining`) : '',
      stateBadge: { Winning: 'Selected', NotSelected: 'Not selected', Closed: 'Closed' }[pkg.packageState] || '',
      mine,
      funding: isObject(pkg.funding) ? pkg.funding : null,
      portionMl: pkg.portionMl,
      pricePerPortion: pkg.pricePerPortion
    };
  }

  function mineView(entry) {
    if (!isObject(entry) || entry.participantPackageStatus !== 'Committed' || !(entry.committedPortions > 0)) {
      return null;
    }
    const reserve = reserveParts(entry.reserve);
    return {
      quantity: entry.committedPortions,
      quantityText: plural(entry.committedPortions, 'portion'),
      positionsText: positionsPhrase(entry.primaryRanges),
      reserveText: reserve
        ? [`${reserve.portions} in reserve`, reserve.positions.toLowerCase(), reserve.ahead].filter(Boolean).join(' · ')
        : '',
      provisional: entry.positionIsProvisional === true
    };
  }

  function rangeText(potential) {
    if (!isObject(potential) || !isMoney(potential.min) || !isMoney(potential.max)) return '';
    return potential.min.cents === potential.max.cents
      ? formatMoney(potential.min)
      : `${formatMoney(potential.min)} – ${formatMoney(potential.max)}`;
  }

  // ===== Refund-aware allocations (backend 3d4cb56) ===========================================

  const ARCHIVE_REFERENCE_STATUSES = ['Assigned', 'PendingConfirmation', 'NoneRetained', 'NotAssigned'];

  /**
   * One allocation, read refund-aware. customerStatus (Paid, PartiallyRefunded, Refunded,
   * PaymentUnderReview, Offered, …) names what the customer sees; originalQuantity, refundedQuantity
   * and retainedQuantity say how many portions were bought, refunded and are still theirs. A response
   * without these fields (an older backend) falls back to the legacy status and quantity, which keep
   * their original meaning (status stays "Paid" for a partly refunded allocation; quantity is the
   * quantity originally allocated). Nothing here is computed from money.
   */
  function allocationFacts(allocation) {
    const a = isObject(allocation) ? allocation : {};
    const aware = typeof a.customerStatus === 'string' && a.customerStatus !== '';
    const legacy = typeof a.status === 'string' ? a.status : '';
    const quantity = isCount(a.quantity) ? a.quantity : 0;
    const referencesStatus = aware && ARCHIVE_REFERENCE_STATUSES.includes(a.archiveReferencesStatus) ? a.archiveReferencesStatus : null;
    const references = Array.isArray(a.archiveReferences) ? a.archiveReferences.filter((r) => typeof r === 'string' && r) : [];
    return {
      aware,
      status: aware ? a.customerStatus : legacy,
      legacyStatus: legacy,
      original: aware && isCount(a.originalQuantity) && a.originalQuantity > 0 ? a.originalQuantity : quantity,
      refunded: aware && isCount(a.refundedQuantity) ? a.refundedQuantity : (legacy === 'Refunded' ? quantity : 0),
      retained: aware && isCount(a.retainedQuantity) ? a.retainedQuantity : (['Paid', 'Offered', 'Preparing', 'PaymentUnderReview'].includes(legacy) ? quantity : 0),
      // null: an older backend - its references are shown as sent.
      referencesStatus,
      // Assigned lists exactly the retained portions' references; no other status lists any.
      references: referencesStatus === null || referencesStatus === 'Assigned' ? references : [],
      payment: isObject(a.payment) ? a.payment : null,
      refunds: Array.isArray(a.refunds) ? a.refunds.filter((r) => isObject(r) && isCount(r.quantity)) : []
    };
  }

  /** Paid, and some of it still the customer's (Paid or PartiallyRefunded with retained portions). */
  const holdsPaidPortions = (facts) => (facts.status === 'Paid' || facts.status === 'PartiallyRefunded') && facts.retained > 0;

  /** Every paid portion refunded: nothing will be prepared or delivered. */
  const fullyRefunded = (facts) => facts.status === 'Refunded' ||
    ((facts.status === 'Paid' || facts.status === 'PartiallyRefunded') && facts.aware && facts.retained === 0 && facts.refunded > 0);

  /**
   * The recorded Shopify figures of one paid allocation, grouped for display. Only what the server
   * recorded is shown (null figures are hidden) and nothing is added up here: the portion subtotal is
   * The Whisky Archive's price × the original quantity; every other amount is Shopify's own.
   *   paid:     { value, sub } - what Shopify charged, with its parts (portions, delivery, voucher)
   *   refunded: { value, sub } - the refunded total and its parts, when anything was refunded
   *   net:      value           - net paid, when the server reports it after a refund
   */
  function paymentFigures(payment) {
    if (!isObject(payment)) return null;
    const money = (m) => (isMoney(m) ? formatMoney(m) : '');
    const positive = (m) => isMoney(m) && m.cents > 0;
    const codes = Array.isArray(payment.discountCodes) ? payment.discountCodes.filter((c) => typeof c === 'string' && c) : [];
    const parts = [];
    if (isMoney(payment.portionSubtotal)) parts.push(`portions ${money(payment.portionSubtotal)}`);
    if (isMoney(payment.shipping)) parts.push(`delivery ${money(payment.shipping)}`);
    if (positive(payment.discounts)) parts.push(`${codes.length ? `voucher ${codes.join(', ')}` : 'discount'} −${money(payment.discounts)}`);
    if (positive(payment.tax)) parts.push(`tax ${money(payment.tax)}`);
    const paid = isMoney(payment.shopifyTotalPaid)
      ? { label: 'Paid through Shopify', value: money(payment.shopifyTotalPaid), sub: parts.join(' · ') }
      : (isMoney(payment.portionSubtotal) ? { label: 'Portion subtotal', value: money(payment.portionSubtotal), sub: parts.slice(1).join(' · ') } : null);
    const refundParts = [];
    if (positive(payment.refundedPortionAmount)) refundParts.push(`portions ${money(payment.refundedPortionAmount)}`);
    if (positive(payment.refundedShipping)) refundParts.push(`delivery ${money(payment.refundedShipping)}`);
    const refunded = positive(payment.refundedTotal) ? { value: money(payment.refundedTotal), sub: refundParts.join(' · ') } : null;
    const net = refunded && isMoney(payment.netPaid) ? money(payment.netPaid) : '';
    return { paid, refunded, net };
  }

  /**
   * The Archive reference line for portions the customer still holds (archiveReferencesStatus):
   * Assigned - exactly the retained portions' references; PendingConfirmation - a refund after opening
   * not yet matched to a reference, so only the retained count; NotAssigned - references come when the
   * bottle is opened; NoneRetained - nothing (no portion is prepared or delivered). An older backend
   * (no status) keeps its references as sent, shown only once the bottle has been opened.
   */
  function referenceLine(facts, opened) {
    if (!holdsPaidPortions(facts)) return null;
    const many = facts.references.length > 1;
    switch (facts.referencesStatus) {
      case 'Assigned':
        return facts.references.length ? { label: many ? 'Archive references' : 'Archive reference', value: facts.references.join(', '), sub: 'Shown on your decant labels' } : null;
      case 'PendingConfirmation':
        return { label: 'Archive reference', value: `${plural(facts.retained, 'portion')} retained`, sub: 'Your Archive reference is being confirmed after a refund.' };
      case 'NotAssigned':
        return { label: 'Archive reference', value: 'Assigned when the bottle is opened', sub: '' };
      case 'NoneRetained':
        return null;
      default:
        return opened && facts.references.length ? { label: 'Archive reference', value: facts.references.join(', '), sub: 'Shown on your decant labels' } : null;
    }
  }

  function allocationLines(allocation, pkg) {
    const lines = [];
    const facts = allocationFacts(allocation);
    const size = (n) => (pkg ? `${plural(n, 'portion')} · ${n} × ${pkg.portionMl} ml` : plural(n, 'portion'));
    const paidLike = holdsPaidPortions(facts);
    const refundedAll = fullyRefunded(facts);
    if (refundedAll) {
      lines.push({ label: 'Refunded allocation', value: size(facts.original), sub: 'Every portion refunded · none retained' });
    } else if (paidLike && facts.refunded > 0) {
      // A partial refund: what was bought, what was refunded, what is still the customer's.
      lines.push({ label: 'Your portions', value: `${plural(facts.retained, 'portion')} retained`, sub: `${facts.original} purchased · ${facts.refunded} refunded` });
    } else {
      lines.push({
        label: paidLike ? 'Paid allocation' : 'Your allocation',
        value: size(facts.original),
        // A promotion from the reserve queue says so, as My Shared Pours does.
        sub: [allocation.source === 'PromotedFromReserve' ? 'Offered from the reserve queue' : '', positionsPhrase(allocation.positions)].filter(Boolean).join(' · ')
      });
    }

    const figures = (paidLike || refundedAll) ? paymentFigures(facts.payment) : null;
    if (figures) {
      if (figures.paid) lines.push({ label: figures.paid.label, value: figures.paid.value, sub: figures.paid.sub });
      if (figures.refunded) lines.push({ label: 'Refunded', value: figures.refunded.value, sub: figures.refunded.sub });
      if (figures.net) lines.push({ label: 'Net paid', value: figures.net, sub: '' });
    } else if (isMoney(allocation.total) && !refundedAll) {
      // Unpaid (or an older backend): the server's total is the portion subtotal (unit price ×
      // quantity); delivery is added and any voucher applied at Shopify checkout. For a refund the
      // amount is Shopify's and is shown only from the recorded payment above.
      lines.push({
        label: paidLike ? 'Paid' : 'Portion subtotal',
        value: formatMoney(allocation.total),
        sub: isMoney(allocation.unitPrice) ? `${facts.original} × ${formatMoney(allocation.unitPrice)} per portion` : ''
      });
    }
    return lines;
  }

  function reserveLine(entry) {
    const reserve = isObject(entry) ? reserveParts(entry.reserve) : null;
    if (!reserve) return null;
    return {
      label: 'Reserve queue',
      value: reserve.portions,
      sub: [reserve.positions, reserve.ahead, 'pay only if offered'].filter(Boolean).join(' · ')
    };
  }

  /**
   * The customer's own position in the winning option, from the server's statuses and allocation
   * quantities only (never the package totals or what was originally requested):
   *   Offered            allocation confirmed, payment due before its deadline
   *   PaymentUnderReview the payment is being checked
   *   Preparing          allocation confirmed, payment link being prepared
   *   Paid               the allocation is secured (the only "congratulations")
   *   Finalising + primary portions: provisional - in the filled option, confirmed next
   *   reserve places     the reserve position, never a promise of an allocation
   * Anything else (not selected, withdrawn, declined, expired, released) has no inset.
   */
  function youView(event, winner, options) {
    const me = event.me;
    if (!winner || !isObject(me) || me.isParticipant !== true) return null;
    const allocations = (Array.isArray(me.allocations) ? me.allocations : [])
      .filter((allocation) => isObject(allocation) && allocation.packageId === winner.id && isCount(allocation.quantity) && allocation.quantity > 0);
    // Counted by customerStatus and the portions still the customer's (refunded portions never count).
    const portions = (status) => allocations.map(allocationFacts)
      .filter((facts) => (status === 'Paid' ? holdsPaidPortions(facts) : facts.status === status))
      .reduce((sum, facts) => sum + facts.retained, 0);
    const name = winner.name;
    const count = (n) => `${n} ${name} ${n === 1 ? 'portion' : 'portions'}`;

    const offered = allocations.find((allocation) => allocationFacts(allocation).status === 'Offered');
    if (offered) {
      const due = formatDateTime(offered.expiresAtUtc, options.timeZone);
      return { tone: 'action', title: `Your allocation is confirmed: ${count(portions('Offered'))}.`, text: due ? `Complete payment before ${due} to keep them.` : 'Complete payment to keep them.' };
    }
    if (portions('PaymentUnderReview')) {
      return { tone: 'pending', title: `Your allocation is confirmed: ${count(portions('PaymentUnderReview'))}.`, text: 'We’re checking your payment. You don’t need to do anything right now.' };
    }
    if (portions('Preparing')) {
      return { tone: 'pending', title: `Your allocation is confirmed: ${count(portions('Preparing'))}.`, text: 'Your secure payment link is being prepared.' };
    }
    if (portions('Paid')) {
      return { tone: 'success', title: 'Congratulations! Your allocation is secured.', text: `You’ve secured ${count(portions('Paid'))}.` };
    }

    const entry = (Array.isArray(me.packages) ? me.packages : []).find((candidate) => isObject(candidate) && candidate.packageId === winner.id);
    if (!entry) return null;
    const reserve = isObject(entry.reserve) && entry.reserve.portions > 0 ? entry.reserve : null;
    const firstReserve = reserve ? validRanges(reserve.ranges)[0] : null;
    const reserveTitle = firstReserve ? `You’re #${firstReserve.from} in the ${name} reserve queue.` : `You’re in the ${name} reserve queue.`;
    const reserveText = 'A reserve place is offered only if an allocated portion is declined or left unpaid, so an allocation isn’t guaranteed.';

    if (event.publicState === 'Finalising' && entry.participantPackageStatus === 'Committed' && isCount(entry.primaryPortions) && entry.primaryPortions > 0) {
      const n = entry.primaryPortions;
      const also = reserve ? ` Your other ${plural(reserve.portions, 'portion')} ${reserve.portions === 1 ? 'is' : 'are'} in the reserve queue${firstReserve ? ` (from #${firstReserve.from})` : ''}.` : '';
      return { tone: 'pending', title: `Your ${n} ${n === 1 ? 'portion is' : 'portions are'} in the filled option.`, text: `Your allocation will be confirmed next. No payment has been taken.${also}` };
    }
    if (reserve && (entry.participantPackageStatus === 'InReserve' || (event.publicState === 'Finalising' && entry.participantPackageStatus === 'Committed'))) {
      return { tone: 'muted', title: reserveTitle, text: reserveText };
    }
    return null;
  }

  function bannerView(event, config, signedIn, recovery = null) {
    const state = event.publicState;
    const kind = state === 'Finalising' ? 'pending' : state === 'Completed' ? 'completed' : RESERVED_STATES.includes(state) ? 'reserved' : null;
    if (!kind) return null;
    const copy = BANNER_COPY[kind];
    // The invitation needs both: the bottle not yet reserved for Shared Pour (Finalising: a normal
    // order is still accepted and closes the Shared Pour) and Shopify reporting it available.
    const invite = kind === 'pending' && config.bottleAvailable === true;
    const members = kind === 'pending' ? memberCount(event) : null;
    const first = members === 1 ? { strong: '1 Archive member', text: copy.memberLine }
      : members ? { strong: `${members} Archive members`, text: copy.membersLine }
        : { strong: '', text: copy.lines[0] };
    const lines = (kind === 'pending' ? [first, { strong: '', text: invite ? copy.openLine : copy.closedLine }] : copy.lines.map((text) => ({ strong: '', text })));
    return {
      kind,
      badge: copy.badge,
      headline: copy.headline,
      tagline: invite && copy.tagline ? copy.tagline : '',
      lines,
      invite: invite ? BANNER_INVITE : null,
      signIn: signedIn ? null : (recovery ? recoverySignIn(recovery) : BANNER_SIGN_IN)
    };
  }

  /**
   * Picks the one status message for a signed-in customer, from the statuses the server returned.
   * context.recovery: the open recovery window (recoveryView); context.joinReason: the server's reason
   * a commitment to available portions is refused for this customer; context.payReason: why payment
   * is not allowed now (actions.pay.reasonCode), when it is not.
   */
  function memberStatus(event, winner, packagesById, options, context = {}) {
    const me = event.me;
    if (!isObject(me)) return null;
    const allocations = Array.isArray(me.allocations) ? me.allocations : [];
    const entries = Array.isArray(me.packages) ? me.packages : [];
    const winnerEntry = winner ? entries.find((entry) => entry.packageId === winner.id) : null;
    const reserve = reserveLine(winnerEntry);
    // Shown in each option's row ("Yours: …"), never repeated here.
    const notSelected = entries.filter((entry) => entry.participantPackageStatus === 'NotSelected');
    const withReserve = (lines) => (reserve ? lines.concat(reserve) : lines);
    const winnerName = winner ? winner.name : 'The selected option';
    // By customerStatus (an older backend: the legacy status). A refund follow-up after opening keeps the
    // legacy status "PaymentUnderReview", but the customer sees PartiallyRefunded - never a payment check.
    const find = (status) => allocations.find((allocation) => allocationFacts(allocation).status === status);

    const offered = find('Offered');
    if (offered) {
      const due = formatDateTime(offered.expiresAtUtc, options.timeZone);
      // Awaiting payment, but the server does not allow it now (e.g. PaymentsUnavailable while
      // commerce is off): say so, rather than asking for a payment no button can make.
      const unavailable = context.payReason ? ` ${paymentMessage(context.payReason)}` : '';
      return {
        tone: 'action',
        eyebrow: 'Payment required',
        title: `Secure your ${winnerName} allocation`,
        body: `${due ? `Pay by ${due} to confirm your portions. ` : ''}Payment is completed through secure Shopify checkout. ${checkoutNote(event.pilot)}${unavailable}`,
        lines: withReserve(allocationLines(offered, packagesById[offered.packageId]))
      };
    }

    const underReview = find('PaymentUnderReview');
    if (underReview) {
      return {
        tone: 'neutral',
        eyebrow: 'Payment under review',
        title: 'We’re checking your payment',
        body: 'The Archive is reviewing this payment. You don’t need to do anything right now.',
        lines: allocationLines(underReview, packagesById[underReview.packageId])
      };
    }

    const preparing = find('Preparing');
    if (preparing) {
      return {
        tone: 'neutral',
        eyebrow: 'Allocation confirmed',
        title: `${winnerName} is the selected option`,
        body: 'Your allocation is confirmed. Your secure payment link is being prepared.',
        lines: withReserve(allocationLines(preparing, packagesById[preparing.packageId]))
      };
    }

    // Paid, with portions still the customer's (Paid or PartiallyRefunded): only those retained portions
    // are ever described as secured, prepared or delivered.
    const paid = allocations.find((allocation) => holdsPaidPortions(allocationFacts(allocation)));
    if (paid) {
      const facts = allocationFacts(paid);
      const opened = event.publicState === 'Preparing' || event.publicState === 'Completed';
      const lines = withReserve(allocationLines(paid, packagesById[paid.packageId]));
      const reference = referenceLine(facts, opened);
      if (reference) lines.push(reference);
      const partly = facts.refunded > 0
        ? ` Part of your payment was refunded through Shopify: ${plural(facts.retained, 'portion')} ${facts.retained === 1 ? 'remains' : 'remain'} yours.`
        : '';
      if (opened) {
        const stage = isObject(paid.fulfilment) ? FULFILMENT_LABELS[paid.fulfilment.stage] : '';
        return {
          tone: event.publicState === 'Completed' ? 'success' : 'neutral',
          eyebrow: stage || STATE_LABELS[event.publicState],
          title: event.publicState === 'Completed' ? 'This Shared Pour is complete' : (facts.retained === 1 ? 'Your portion is being prepared' : 'Your portions are being prepared'),
          body: (event.publicState === 'Completed' ? 'Thank you for pouring with the Archive.' : 'Each portion is measured, sealed and labelled before it is sent.') + partly,
          lines
        };
      }
      if (event.publicState === 'FullyFunded') {
        return { tone: 'success', eyebrow: 'Fully funded', title: 'Every portion is paid', body: `The bottle will now be opened and decanted under Archive supervision.${partly}`, lines };
      }
      return { tone: 'success', eyebrow: 'Paid', title: facts.retained === 1 ? 'Your portion is secured' : 'Your portions are secured', body: `Payment confirms your portions. The bottle stays sealed until every portion is paid.${partly}`, lines };
    }

    // Fully refunded (customerStatus Refunded, or every paid portion refunded): nothing is payable,
    // prepared or delivered.
    const refunded = allocations.find((allocation) => fullyRefunded(allocationFacts(allocation)));
    if (refunded) {
      return {
        tone: 'muted',
        eyebrow: 'Refunded',
        title: 'Your payment has been refunded',
        body: 'These portions were refunded through Shopify, so they won’t be prepared or sent.',
        lines: allocationLines(refunded, packagesById[refunded.packageId])
      };
    }

    if (winnerEntry && winnerEntry.participantPackageStatus === 'InReserve') {
      return {
        tone: 'neutral',
        eyebrow: 'Reserve queue',
        title: `You’re in the ${winnerName} reserve queue`,
        body: 'This is a place in the queue, not a purchase. If an allocated portion is declined or left unpaid, it is offered to the reserve queue in order. It may never be offered.',
        lines: (reserve ? [reserve] : []).concat(isMoney(winner && winner.pricePerPortion) ? [{ label: 'Price if offered', value: formatMoney(winner.pricePerPortion), sub: 'per portion' }] : [])
      };
    }

    if (event.publicState === 'Finalising' && winnerEntry && winnerEntry.participantPackageStatus === 'Committed') {
      return {
        tone: 'neutral',
        eyebrow: 'Finalising',
        title: `${winnerName} has filled`,
        body: 'The Archive is securing the bottle and confirming allocations. No payment has been taken.',
        lines: []
      };
    }

    const ended = entries.find((entry) => ['AllocationDeclined', 'AllocationExpired', 'ReserveWithdrawn', 'ReserveNotRequired', 'Refunded'].includes(entry.participantPackageStatus));
    if (ended) {
      const copy = {
        // Never says who received the portions: the reserve queue, other members, or nobody yet.
        AllocationDeclined: ['Allocation declined', 'Your allocation was released', 'You declined this allocation, so its portions were released. Nothing has been charged.'],
        AllocationExpired: ['Payment window closed', 'Your allocation has expired', 'The payment deadline passed, so your portions were offered to the reserve queue. Nothing has been charged.'],
        ReserveWithdrawn: ['Reserve queue', 'You’ve left the reserve queue', 'Your reserve portions were withdrawn. Nothing has been charged.'],
        ReserveNotRequired: ['Reserve queue', 'Your reserve portions weren’t needed', 'Every portion was paid, so your reserve portions were released at no charge.'],
        // Normally the refunded allocation's own panel (above) says this; here when it is not listed.
        Refunded: ['Refunded', 'Your payment has been refunded', 'Your portions were refunded through Shopify, so they won’t be prepared or sent.']
      }[ended.participantPackageStatus];
      // Portions have become available, but the server refuses this customer: say why, once.
      const available = context.recovery || context.portionsAvailable;
      const barred = available && context.joinReason === 'PaymentExpiredNotEligible'
        ? ' Because your payment deadline passed, you can’t commit to the portions that have become available.'
        : available && context.joinReason === 'AllocationDeclinedNotEligible' ? ` ${ERROR_COPY.AllocationDeclinedNotEligible}` : '';
      return { tone: 'muted', eyebrow: copy[0], title: copy[1], body: copy[2] + barred, lines: [] };
    }

    if (notSelected.length) {
      return {
        tone: 'muted',
        eyebrow: 'Not selected',
        title: `${winnerName} filled first`,
        body: 'Your chosen options were not selected, so those commitments were released at no charge.',
        lines: []
      };
    }

    return null;
  }

  /**
   * event.recoveryWindow (Phase 2): released portions are open to signed-in customers until
   * closesAtUtc. Used only when the server sends a window that is still open at the server's own time
   * with at least one available portion; whether THIS customer may commit is the server's
   * actions.joinReserve, never decided here. availablePortions never includes reserve slots.
   */
  function recoveryView(event, serverNowUtc, options) {
    const window = isObject(event.recoveryWindow) ? event.recoveryWindow : null;
    if (!window || typeof window.closesAtUtc !== 'string') return null;
    const closes = Date.parse(window.closesAtUtc);
    const now = Date.parse(serverNowUtc);
    if (!Number.isFinite(closes) || (Number.isFinite(now) && closes <= now)) return null;
    if (!Number.isInteger(window.availablePortions) || window.availablePortions < 1) return null;
    const closesText = formatDateTime(window.closesAtUtc, options.timeZone);
    if (!closesText) return null;
    return { closesAtUtc: window.closesAtUtc, closesText, available: window.availablePortions };
  }

  const recoveryCount = (recovery, name) =>
    `${recovery.available} ${name ? `${name} ` : ''}${recovery.available === 1 ? 'portion' : 'portions'}`;

  /**
   * Portions the server lets this customer claim now (joinReserve.claimsAvailablePortions and
   * allowed): an immediate allocation, not a reserve place. available: the recovery window's count,
   * else the server's availablePortions; max: the server's maxPortions, never above what is
   * available; min: the server's minPortions. null when there is nothing the customer could claim.
   */
  function claimView(join, recovery) {
    if (!join.allowed || !join.claims) return null;
    const available = recovery ? recovery.available : join.available;
    const max = available > 0 ? Math.min(join.max, available) : join.max;
    if (max < 1 || max < join.min) return null;
    return { available, max, min: join.min, closesText: recovery ? recovery.closesText : '' };
  }

  function claimCount(claim, name) {
    return `${claim.available} ${name ? `${name} ` : ''}${claim.available === 1 ? 'portion' : 'portions'}`;
  }

  /** A signed-in customer without commitments whom the server allows to claim available portions. */
  function claimStatus(winner, claim) {
    const name = winner ? winner.name : '';
    const lines = [];
    if (claim.available > 0) lines.push({ label: 'Available now', value: plural(claim.available, 'portion'), sub: claim.closesText ? `Until ${claim.closesText}` : '' });
    if (claim.min > 1) lines.push({ label: 'Minimum claim', value: plural(claim.min, 'portion'), sub: '' });
    if (winner && isMoney(winner.pricePerPortion)) lines.push({ label: 'Price', value: formatMoney(winner.pricePerPortion), sub: 'per portion' });
    return {
      tone: 'neutral',
      eyebrow: 'Portions available',
      title: claim.available > 0 ? `${claimCount(claim, name)} ${claim.available === 1 ? 'is' : 'are'} available now` : 'Portions are available now',
      body: `${claim.closesText ? `You can claim them until ${claim.closesText}. ` : ''}${CLAIM_NOTE} Nothing is charged until you pay.`,
      lines
    };
  }

  const CLAIM_NOTE = 'Claimed portions are allocated to you straight away, and you’re then invited to pay through secure Shopify checkout before the deadline shown.';

  /** A signed-in visitor without commitments whom the server allows to commit to available portions. */
  function recoveryStatus(winner, recovery) {
    const name = winner ? winner.name : '';
    return {
      tone: 'neutral',
      eyebrow: 'Portions available',
      title: `${recoveryCount(recovery, name)} ${recovery.available === 1 ? 'has' : 'have'} become available`,
      body: `You can commit to them until ${recovery.closesText}. Available portions are allocated in commitment order, and you’re then invited to pay through secure Shopify checkout. Nothing is charged until you pay.`,
      lines: [{ label: 'Available now', value: plural(recovery.available, 'portion'), sub: `Until ${recovery.closesText}` }]
        .concat(winner && isMoney(winner.pricePerPortion) ? [{ label: 'Price', value: formatMoney(winner.pricePerPortion), sub: 'per portion' }] : [])
    };
  }

  /** Guests while portions are available: Shopify sign-in, without promising they may commit. */
  function recoverySignIn(recovery) {
    return {
      title: 'Portions have become available',
      text: `Sign in to see whether you can commit to them before ${recovery.closesText}.`,
      label: 'Sign in'
    };
  }

  function publicStatus(event, winner) {
    const winnerName = winner ? winner.name : 'A pour option';
    switch (event.publicState) {
      case 'Finalising':
        return { tone: 'neutral', eyebrow: 'Finalising', title: `${winnerName} has filled`, body: 'The Archive is securing the bottle and confirming allocations.', lines: [] };
      case 'CollectingPayment':
        return { tone: 'neutral', eyebrow: 'Collecting payment', title: `${winnerName} was selected`, body: 'Allocated members are completing payment. The bottle stays sealed until every portion is paid.', lines: [] };
      case 'FullyFunded':
        return { tone: 'success', eyebrow: 'Fully funded', title: 'Every portion is paid', body: 'The bottle will be opened and decanted under Archive supervision.', lines: [] };
      case 'Preparing':
        return { tone: 'neutral', eyebrow: 'Being prepared', title: 'The bottle has been opened', body: 'Portions are being decanted, sealed and labelled.', lines: [] };
      case 'Completed':
        return { tone: 'success', eyebrow: 'Complete', title: 'This Shared Pour is complete', body: 'Every portion has been sent.', lines: [] };
      case 'Cancelled':
        return event.stateReason === 'BottleSold'
          ? { tone: 'muted', eyebrow: 'Closed', title: 'This bottle has been sold', body: 'The Shared Pour has closed and nobody has been charged.', lines: [] }
          : { tone: 'muted', eyebrow: 'Closed', title: 'This Shared Pour has closed', body: 'Commitments have been released.', lines: [] };
      case 'Closed':
        return winner
          ? { tone: 'muted', eyebrow: 'Closed', title: 'This Shared Pour has closed', body: '', lines: [] }
          : { tone: 'muted', eyebrow: 'Closed', title: 'Commitments have closed', body: 'No pour option filled before commitments closed. Nothing has been charged.', lines: [] };
      default:
        return null;
    }
  }

  /** What the server says this customer may do, keyed for the controls. Never inferred locally. */
  function actionsView(me) {
    const actions = isObject(me) && isObject(me.actions) ? me.actions : {};
    const flag = (name) => Boolean(isObject(actions[name]) && actions[name].allowed);
    const commit = isObject(actions.commit) ? actions.commit : {};
    const limits = {};
    (Array.isArray(commit.packageLimits) ? commit.packageLimits : []).forEach((limit) => {
      if (isObject(limit) && typeof limit.packageId === 'string') {
        limits[limit.packageId] = {
          allowed: limit.allowed === true,
          reasonCode: typeof limit.reasonCode === 'string' ? limit.reasonCode : null,
          currentQuantity: isCount(limit.currentQuantity) ? limit.currentQuantity : 0,
          maxAdditional: isCount(limit.maxAdditional) ? limit.maxAdditional : 0,
          minTotal: isCount(limit.minTotal) && limit.minTotal > 0 ? limit.minTotal : 1,
          primaryRemaining: isCount(limit.primaryRemaining) ? limit.primaryRemaining : null
        };
      }
    });
    const join = isObject(actions.joinReserve) ? actions.joinReserve : {};
    const withdraw = isObject(actions.withdrawReserve) ? actions.withdrawReserve : {};
    const decline = isObject(actions.declineAllocation) ? actions.declineAllocation : {};
    return {
      canCommit: commit.allowed === true,
      commitReason: typeof commit.reasonCode === 'string' ? commit.reasonCode : null,
      limits,
      canIncrease: flag('increase'),
      canDecrease: flag('decrease'),
      canCancel: flag('cancel'),
      decreaseCutoffUtc: isObject(actions.decrease) ? actions.decrease.cutoffAtUtc : null,
      joinReserve: {
        allowed: join.allowed === true,
        max: isCount(join.maxPortions) ? join.maxPortions : 0,
        reasonCode: typeof join.reasonCode === 'string' ? join.reasonCode : null,
        // Immediate reallocation: true when this action can only claim portions free right now (an
        // allocation at once), never a place in the reserve queue. Absent on older backends.
        claims: join.claimsAvailablePortions === true,
        available: isCount(join.availablePortions) ? join.availablePortions : 0,
        min: isCount(join.minPortions) && join.minPortions > 0 ? join.minPortions : 1
      },
      withdrawReserve: { allowed: withdraw.allowed === true, portions: isCount(withdraw.portions) ? withdraw.portions : 0 },
      decline: {
        allowed: decline.allowed === true && Array.isArray(decline.allocationIds) && decline.allocationIds.length > 0,
        allocationId: Array.isArray(decline.allocationIds) && typeof decline.allocationIds[0] === 'string' ? decline.allocationIds[0] : null
      },
      pay: payPermission(actions.pay)
    };
  }

  /**
   * The server's pay permission: only allocation ids it lists in actions.pay.allocationIds, and only
   * when allowed is true. Nothing is inferred from statuses, dates or countdowns.
   */
  function payPermission(pay) {
    const ids = isObject(pay) && pay.allowed === true && Array.isArray(pay.allocationIds)
      ? pay.allocationIds.filter((id) => typeof id === 'string' && PAYMENT_ID.test(id))
      : [];
    return { allowed: ids.length > 0, allocationIds: ids, reasonCode: isObject(pay) && typeof pay.reasonCode === 'string' ? pay.reasonCode : null };
  }

  /**
   * Once commitments have closed: every option other than the winner (or every option when none
   * filled), with the commitment it reached. Recorded history, never a charge: an option that did
   * not fill is never charged, and the copy says so.
   */
  function resultsView(packages, winner, known, me) {
    const entries = isObject(me) && Array.isArray(me.packages) ? me.packages : [];
    const rows = packages.filter((pkg) => !winner || pkg.id !== winner.id).map((pkg) => {
      const atClose = known ? ' at close' : '';
      // The signed-in customer's own commitment to this option (their data only, from me).
      const entry = entries.find((candidate) => isObject(candidate) && candidate.packageId === pkg.id &&
        ['NotSelected', 'Closed'].includes(candidate.participantPackageStatus) && isCount(candidate.committedPortions) && candidate.committedPortions > 0);
      return {
        id: pkg.id,
        name: pkg.name,
        badge: pkg.stateBadge,
        yours: entry ? `Yours: ${plural(entry.committedPortions, 'portion')}${known ? ' · released, no charge' : ''}` : '',
        progress: pkg.progressAvailable ? {
          percent: pkg.percent,
          now: pkg.committed,
          max: pkg.target,
          label: `${pkg.name}: portions committed${atClose}`,
          text: `${pkg.committed} of ${plural(pkg.target, 'portion')} committed${atClose}`,
          shown: `${pkg.committed} of ${pkg.target} committed${atClose}`
        } : null
      };
    });
    if (!rows.length) return null;
    return {
      heading: winner ? (rows.length === 1 ? 'Other pour option' : 'Other pour options') : 'Pour options',
      rows,
      // With no winner the status panel already says nothing was charged.
      note: known && winner ? 'Options that did not fill are never charged.' : ''
    };
  }

  function buildViewModel(response, config = {}, options = {}) {
    const event = response.event;
    const me = isObject(event.me) ? event.me : null;
    const entries = me && Array.isArray(me.packages) ? me.packages : [];
    const entryFor = (id) => entries.find((entry) => entry && entry.packageId === id);
    // A state this version does not know is read-only: the server's action flags are not read at
    // all, so nothing can become available by accident.
    const known = PUBLIC_STATES.includes(event.publicState);
    const actions = actionsView(known ? me : null);

    const packages = event.packages.map((pkg) => {
      const view = packageView(pkg, config, mineView(entryFor(pkg.packageId)));
      view.limit = actions.limits[pkg.packageId] || null;
      view.currentQuantity = view.limit ? view.limit.currentQuantity : (view.mine ? view.mine.quantity : 0);
      return view;
    });
    const packagesById = Object.fromEntries(packages.map((pkg) => [pkg.id, pkg]));
    const winner = packagesById[event.winningPackageId] || packages.find((pkg) => pkg.stateBadge === 'Selected') || null;

    const selecting = known && SELECTING_STATES.includes(event.publicState);
    const closedWithoutWinner = event.publicState === 'Closed' && !winner;
    const phase = selecting ? 'selecting' : (closedWithoutWinner ? 'closed' : 'post');
    const participant = Boolean(me && me.isParticipant);
    const viewer = !me ? 'guest' : (participant ? 'participant' : 'member');
    // An open recovery window (Phase 2), and whether the server lets this signed-in visitor without
    // commitments commit to its available portions (the existing post-lock join action).
    // The backend opens windows only while collecting payment (locked, before full funding).
    const recovery = event.publicState === 'CollectingPayment' && winner ? recoveryView(event, response.serverNowUtc, options) : null;
    // Portions this customer may claim right now (the server's claimsAvailablePortions), with or
    // without a recovery window. Quantity and range come from the server only.
    const claim = known && event.publicState === 'CollectingPayment' && winner ? claimView(actions.joinReserve, recovery) : null;
    const recoveryJoin = Boolean((claim || (recovery && !actions.joinReserve.claims)) && viewer === 'member' && actions.joinReserve.allowed);
    // Which presentation: the existing selection UI while open; for visitors without commitments a
    // compact banner (or nothing, once closed or cancelled) - or the panel while the server lets them
    // commit to available portions; for participants the detailed panel. A state this version does
    // not know keeps the read-only panel.
    const layout = phase === 'selecting' ? 'selecting'
      : !known || participant || recoveryJoin ? 'panel'
        : HIDDEN_FOR_VISITORS.includes(event.publicState) ? 'hidden'
          : 'banner';
    const timeline = isObject(event.timeline) ? event.timeline : {};

    let timelineNote = '';
    if (event.publicState === 'Upcoming' && timeline.opensAtUtc) {
      timelineNote = `Opens ${formatDateTime(timeline.opensAtUtc, options.timeZone)}`;
    } else if (event.publicState === 'Open' && timeline.commitmentClosesAtUtc) {
      timelineNote = `Commitments close ${formatDateTime(timeline.commitmentClosesAtUtc, options.timeZone)}`;
    }

    const canSelect = phase === 'selecting' && viewer === 'member' && actions.canCommit;
    const canManage = phase === 'selecting' && viewer === 'participant' &&
      (actions.canIncrease || actions.canDecrease || actions.canCancel || actions.canCommit);

    let footer = null;
    if (phase === 'selecting') {
      if (viewer === 'guest' && response.signInRequiredToCommit !== false) {
        footer = { kind: 'gate' };
      } else if (viewer === 'participant') {
        const active = entries.filter((entry) => mineView(entry));
        footer = {
          kind: 'participant',
          // The pilot is paused (every action PilotPaused): say why nothing can be managed.
          paused: actions.commitReason === 'PilotPaused',
          countText: `${plural(isCount(me.totalCommittedPortions) ? me.totalCommittedPortions : 0, 'portion')} across ${plural(active.length, 'pour option')}`,
          rangeText: rangeText(me.potentialCommitment),
          isRange: isObject(me.potentialCommitment) && isMoney(me.potentialCommitment.min) && isMoney(me.potentialCommitment.max) &&
            me.potentialCommitment.min.cents !== me.potentialCommitment.max.cents
        };
      } else if (viewer === 'member') {
        footer = canSelect ? { kind: 'select' } : { kind: 'unavailable', reasonCode: actions.commitReason };
      }
    }

    let status = null;
    if (!known) {
      status = null;
    } else if (phase === 'closed') {
      status = publicStatus(event, null);
    } else if (phase === 'post') {
      status = memberStatus(event, winner, packagesById, options, {
        recovery,
        // Portions are free now (a recovery window, or the server's availablePortions to claim).
        portionsAvailable: Boolean(recovery) || actions.joinReserve.available > 0,
        joinReason: actions.joinReserve.reasonCode,
        payReason: actions.pay.allowed ? null : actions.pay.reasonCode
      }) ||
        (recoveryJoin ? (claim ? claimStatus(winner, claim) : recoveryStatus(winner, recovery)) : null) ||
        publicStatus(event, winner);
    }

    // One line of context under the heading, as in the prototype.
    const subtitle = typeof event.subtitle === 'string' && event.subtitle.trim() ? event.subtitle.trim() : '';
    const lede = phase === 'selecting' && viewer === 'participant'
      ? 'Your commitments are active.'
      : (subtitle || 'Experience this bottle by the pour.');

    const stage = isObject(event.stage) && Array.isArray(event.stage.labels) &&
      Number.isInteger(event.stage.index) && event.stage.index >= 0 && event.stage.index < event.stage.labels.length
      ? { index: event.stage.index, labels: event.stage.labels.map(String) }
      : null;

    let winnerProgress = null;
    if (winner) {
      if (winner.funding && isCount(winner.funding.portionsRequired) && winner.funding.portionsRequired > 0) {
        winnerProgress = {
          percent: Math.max(0, Math.min(100, winner.funding.percentFunded || 0)),
          text: `${winner.funding.portionsPaid} of ${plural(winner.funding.portionsRequired, 'portion')} paid`,
          sub: winner.funding.fullyFunded ? 'Fully funded' : 'Sealed until every portion is paid',
          label: 'Portions paid',
          now: winner.funding.portionsPaid,
          max: winner.funding.portionsRequired
        };
      } else if (winner.progressAvailable) {
        winnerProgress = {
          percent: winner.percent,
          text: winner.committedText,
          sub: event.publicState === 'Finalising' ? 'Securing the bottle' : '',
          label: 'Portions committed',
          now: winner.committed,
          max: winner.target
        };
      }
    }

    // Post-lock actions are offered only when the server allows them for this customer.
    const postActions = known && phase === 'post' && viewer !== 'guest'
      ? {
        // A claim the customer could not make (no portions within the server's min and max) is not offered.
        joinReserve: actions.joinReserve.allowed && (!actions.joinReserve.claims || claim) ? actions.joinReserve : null,
        withdrawReserve: actions.withdrawReserve.allowed ? actions.withdrawReserve : null,
        decline: actions.decline.allowed ? actions.decline : null,
        pay: actions.pay.allowed ? {
          allocationIds: actions.pay.allocationIds,
          allocations: actions.pay.allocationIds.map((id) => {
            const allocation = (me && Array.isArray(me.allocations) ? me.allocations : []).find((a) => isObject(a) && a.allocationId === id);
            return { id, total: allocation && isMoney(allocation.total) ? allocation.total : null };
          })
        } : null
      }
      : { joinReserve: null, withdrawReserve: null, decline: null, pay: null };

    // The allocation the server lets this customer decline, as the confirmation describes it.
    let declineInfo = null;
    if (postActions.decline) {
      const allocation = (me && Array.isArray(me.allocations) ? me.allocations : [])
        .find((a) => isObject(a) && a.allocationId === postActions.decline.allocationId) || {};
      const pkg = packagesById[allocation.packageId] || null;
      const entry = entryFor(allocation.packageId);
      declineInfo = declineSummary({
        eventTitle: event.title,
        packageName: pkg ? pkg.name : '',
        portionMl: pkg ? pkg.portionMl : null,
        quantity: allocation.quantity,
        unitPrice: allocation.unitPrice,
        total: allocation.total,
        expiresAtUtc: allocation.expiresAtUtc,
        reservePortions: isObject(entry) && isObject(entry.reserve) ? entry.reserve.portions : (isObject(entry) ? entry.reservePortions : 0)
      }, options.timeZone);
    }

    return {
      sharedPourId: event.sharedPourId,
      publicState: event.publicState,
      stateLabel: STATE_LABELS[event.publicState] || '',
      readOnly: !known,
      phase,
      viewer,
      lede,
      descriptionParagraphs: paragraphs(event.description),
      timelineNote,
      packages,
      packageCount: packages.length,
      winner,
      winnerProgress,
      results: phase === 'selecting' ? null : resultsView(packages, winner, known, me),
      layout,
      pilot: event.pilot === true,
      banner: layout === 'banner' ? bannerView(event, config, Boolean(me), recovery) : null,
      you: layout === 'panel' && known ? youView(event, winner, options) : null,
      recovery,
      claim,
      recoveryJoin,
      // A customer who already holds portions in the winning option adds to them (a top-up).
      recoveryTopUp: Boolean(recovery && winner && entries.some((entry) => isObject(entry) && entry.packageId === winner.id &&
        ['Committed', 'Secured', 'InReserve'].includes(entry.participantPackageStatus))),
      signedIn: Boolean(me),
      stage: phase === 'post' ? stage : null,
      footer,
      status,
      actions,
      postActions,
      declineSummary: declineInfo,
      canSelect,
      canManage,
      termsVersion: isObject(event.terms) && typeof event.terms.version === 'string' ? event.terms.version : null,
      showDisclosure: phase === 'selecting',
      termsUrl: safeUrl(isObject(event.terms) ? event.terms.url : null),
      loginUrl: safeUrl(config.loginUrl) || '/customer_authentication/login',
      mySharedPoursUrl: safeUrl(config.mySharedPoursUrl)
    };
  }

  // ===== Selections (presentation only: the server decides everything on submit) ===============

  /**
   * What the customer has chosen, as counts only. No money is calculated here: the potential
   * commitment is the server's me.potentialCommitment, shown once the Archive has the commitment.
   */
  function selectionCount(vm, quantities) {
    const chosen = vm.packages.filter((pkg) => (quantities[pkg.id] || 0) > 0);
    return {
      portions: chosen.reduce((sum, pkg) => sum + quantities[pkg.id], 0),
      options: chosen.length
    };
  }

  function selectionCountText(count) {
    return `${plural(count.portions, 'portion')} across ${plural(count.options, 'pour option')}`;
  }

  /** Bounds for choosing a NEW commitment in one option (server limits). */
  function selectBounds(pkg) {
    const limit = pkg.limit;
    if (!limit || !limit.allowed) return { min: 0, max: 0, first: 0 };
    return { min: 0, max: limit.maxAdditional, first: Math.min(limit.maxAdditional, Math.max(1, limit.minTotal - limit.currentQuantity)) };
  }

  /** Bounds for a participant's NEW TOTAL in one option (server limits and allowed actions). */
  function manageBounds(vm, pkg) {
    const current = pkg.currentQuantity;
    const limit = pkg.limit;
    const canGrow = Boolean(limit && limit.allowed && (current > 0 ? vm.actions.canIncrease : vm.actions.canCommit));
    const max = canGrow ? current + limit.maxAdditional : current;
    let min = current;
    if (current > 0 && vm.actions.canCancel) min = 0;
    else if (current > 0 && vm.actions.canDecrease) min = Math.min(current, limit ? limit.minTotal : 1);
    return { min, max, current };
  }

  /** Steps a quantity within bounds, skipping the values between 0 and a minimum lot. */
  function stepQuantity(value, direction, bounds) {
    const floorWhenChosen = Math.max(bounds.min, bounds.first || 1);
    let next = value + direction;
    if (direction > 0 && value === 0 && bounds.first) next = bounds.first;
    if (direction < 0 && bounds.min === 0 && value <= floorWhenChosen && value > 0 && bounds.first) next = 0;
    return Math.max(bounds.min, Math.min(bounds.max, next));
  }

  // ===== Errors =================================================================================

  // The restricted production pilot is paused (server pilot switch Closing): every action refused.
  const PILOT_PAUSED = 'This Shared Pour is paused. Nothing can be changed or paid right now.';

  const ERROR_COPY = {
    AtPackageCap: 'You have reached the maximum portions for one of these options.',
    AtEventCap: 'This would exceed the maximum portions per member for this Shared Pour.',
    CapacityExhausted: 'There are no longer enough portions available in one of these options. The options have been refreshed.',
    ReserveFull: 'The reserve queue for one of these options is full.',
    BelowMinimumLot: 'One of these options needs a larger number of portions.',
    EventFinalising: 'An option has just filled, so this Shared Pour is no longer taking commitments. It has been refreshed.',
    CommitmentsClosed: 'Commitments for this Shared Pour have closed.',
    NotOpenYet: 'This Shared Pour is not open for commitments yet.',
    TermsVersionMismatch: 'The Shared Pour terms have just been updated. Please review them and confirm again.',
    ConfirmationRequired: 'Please confirm to continue.',
    ReduceCutoffPassed: 'Commitments can no longer be reduced for this Shared Pour.',
    ReductionsNotAllowed: 'Commitments can no longer be reduced for this Shared Pour.',
    ReserveClosed: 'The reserve queue is closed.',
    NoReservePortions: 'You have no portions in the reserve queue.',
    WithdrawalNotAllowed: 'Reserve portions can no longer be withdrawn.',
    NoOfferedAllocation: 'There is no allocation waiting for you.',
    AllocationExpired: 'This allocation has expired.',
    AllocationAlreadyPaid: 'This allocation has already been paid.',
    DeclineNotAllowed: 'This allocation can no longer be declined.',
    SignInRequired: 'Your session has ended. Please sign in again to continue.',
    NotFound: 'This Shared Pour is no longer available.',
    TemporarilyUnavailable: 'We couldn’t reach the Archive just now. Your request may not have been saved — please try again.',
    // Phase 2 (capacity recovery).
    PaymentExpiredNotEligible: 'An earlier allocation of yours in this Shared Pour expired unpaid, so you can’t commit to it again.',
    // Immediate reallocation: a customer who declined an allocation of this option cannot reclaim it.
    AllocationDeclinedNotEligible: 'You declined this allocation, so you can’t claim these portions again.',
    NoCommitmentWindow: 'Commitments for this Shared Pour are closed right now. It has been refreshed.',
    // Restricted production pilot: the server's pilot switch is Closing.
    PilotPaused: PILOT_PAUSED
  };

  // A refused commitment to available portions (recovery window): the same codes, in its own words.
  const RECOVERY_ERROR_COPY = {
    CapacityExhausted: 'Those portions have just been taken, so nothing was committed. The Shared Pour has been refreshed.',
    NoCommitmentWindow: 'The window for available portions has just closed, so nothing was committed. The Shared Pour has been refreshed.'
  };

  // A refused claim of available portions (joinReserve.claimsAvailablePortions): nothing was claimed.
  const CLAIM_ERROR_COPY = {
    CapacityExhausted: 'Those portions have just been taken, so nothing was claimed. The Shared Pour has been refreshed.',
    ReserveFull: 'Those portions have just been taken, so nothing was claimed. The Shared Pour has been refreshed.',
    ReserveClosed: 'Those portions are no longer available to claim. The Shared Pour has been refreshed.',
    NoCommitmentWindow: 'The window for available portions has just closed, so nothing was claimed. The Shared Pour has been refreshed.'
  };

  /** Customer copy for a refused request. MultiplePackagesWouldFill uses the server's approved wording. */
  function errorMessage(reasonCode, serverMessage) {
    if (reasonCode === 'MultiplePackagesWouldFill' && typeof serverMessage === 'string' && serverMessage) return serverMessage;
    return ERROR_COPY[reasonCode] || 'This could not be completed. Please refresh the page and try again.';
  }

  // ===== Decline an allocation (shared with My Shared Pours) ======================================

  // One confirmation for the product page and My Shared Pours. The copy never says another customer
  // is waiting: whether the reserve queue or other members receive the portions is the server's to
  // decide once the decline is applied.
  const DECLINE_COPY = {
    title: 'Decline your allocation?',
    lede: 'You are about to permanently decline your allocated whisky portions. Your payment request will be cancelled, and these portions will be offered to the next eligible customer in the reserve queue, if one exists. Otherwise, they may become available to other eligible members.',
    warning: 'This action cannot be undone.',
    keep: 'Keep allocation',
    confirm: 'Confirm decline',
    busy: 'Declining…',
    success: 'Your allocation has been declined and its portions released. Nothing has been charged.'
  };

  // Phones: the decline dialog's two buttons stay in reach at the foot of the dialog while its
  // details scroll (assets/archive-shared-pour.css).
  const DECLINE_ACTIONS_CLASS = 'asp-dialog__actions--pinned';

  // Outcome unknown (timeout, 5xx - including Shopify checkout retirement that could not be
  // confirmed): retried only under the same key, so it can never apply twice.
  const DECLINE_RETRY_TEXT = 'We couldn’t confirm your decline just now. Your allocation may not have been released — please try again.';

  // A definite refusal ends the decline: the server's state is re-read and the reason shown beside it.
  const DECLINE_STALE = 'This allocation can no longer be declined. Its current status is shown.';
  const DECLINE_REFUSALS = {
    PaymentProcessing: { tone: 'success', text: 'A payment for this allocation has already been completed, so it hasn’t been declined. Your payment is being confirmed.' },
    AllocationAlreadyPaid: { tone: 'success', text: 'This allocation has already been paid, so it hasn’t been declined.' },
    AllocationExpired: { tone: 'error', text: 'The payment deadline for this allocation has passed, so it has already been released.' },
    NoOfferedAllocation: { tone: 'error', text: DECLINE_STALE },
    DeclineNotAllowed: { tone: 'error', text: DECLINE_STALE },
    NotFound: { tone: 'error', text: DECLINE_STALE }
  };

  function declineRefusal(reasonCode, serverMessage) {
    return DECLINE_REFUSALS[reasonCode] || { tone: 'error', text: errorMessage(reasonCode, serverMessage) };
  }

  /**
   * What the decline dialog shows, from the server's own allocation (quantity, unit price, total and
   * payment deadline) and package. A value the server did not send is left out, never estimated.
   */
  function declineSummary(input, timeZone) {
    const source = isObject(input) ? input : {};
    const quantity = isCount(source.quantity) && source.quantity > 0 ? source.quantity : 0;
    const portionMl = isCount(source.portionMl) && source.portionMl > 0 ? source.portionMl : 0;
    return {
      eventTitle: typeof source.eventTitle === 'string' ? source.eventTitle.trim() : '',
      packageName: typeof source.packageName === 'string' && source.packageName.trim() ? source.packageName.trim() : 'Pour option',
      quantityText: quantity ? plural(quantity, 'portion') : '',
      portionText: portionMl ? `${portionMl} ml` : '',
      totalText: isMoney(source.total) ? formatMoney(source.total) : '',
      unitText: quantity && isMoney(source.unitPrice) ? `${quantity} × ${formatMoney(source.unitPrice)} per portion` : '',
      deadlineText: formatDateTime(source.expiresAtUtc, timeZone),
      reservePortions: isCount(source.reservePortions) ? source.reservePortions : 0
    };
  }

  /** The dialog body: the explanation, the allocation being declined, what else follows, the warning. */
  function renderDeclineDetails(summary) {
    const s = summary || declineSummary(null);
    const line = (label, value, sub) => (value
      ? `<div class="asp-panel__line"><dt>${escapeHtml(label)}</dt><dd><span class="asp-panel__value">${escapeHtml(value)}</span>${sub ? `<span class="asp-panel__sub">${escapeHtml(sub)}</span>` : ''}</dd></div>`
      : '');
    const notes = [
      'Nothing has been charged.',
      s.reservePortions ? `Your ${plural(s.reservePortions, 'reserve portion')} in ${s.packageName} will also be withdrawn.` : '',
      `You won’t be able to rejoin ${s.packageName} in this Shared Pour.`
    ].filter(Boolean);
    return `
      <p class="asp-dialog__lede">${escapeHtml(DECLINE_COPY.lede)}</p>
      <div class="asp-decline" data-asp-decline-summary>
        <div class="asp-review__row">
          <div><p class="asp-review__name">${escapeHtml(s.packageName)}</p>${s.eventTitle ? `<p class="asp-review__meta">${escapeHtml(s.eventTitle)}</p>` : ''}</div>
        </div>
        <dl class="asp-panel__lines asp-decline__lines">
          ${line('Portions declined', s.quantityText, '')}
          ${line('Portion size', s.portionText, '')}
          ${line('Allocation total', s.totalText, s.unitText)}
          ${line('Payment deadline', s.deadlineText, '')}
        </dl>
      </div>
      <ul class="asp-notes" role="list">${notes.map((note) => `<li>${escapeHtml(note)}</li>`).join('')}</ul>
      <p class="asp-decline__warning">${escapeHtml(DECLINE_COPY.warning)}</p>`;
  }

  // ===== API =====================================================================================

  function newIdempotencyKey(cryptoImpl) {
    const c = cryptoImpl || (typeof crypto !== 'undefined' ? crypto : null);
    if (c && typeof c.randomUUID === 'function') return `sp-${c.randomUUID()}`;
    const bytes = new Uint8Array(16);
    if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
    else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
    return `sp-${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
  }

  /**
   * One idempotency key per mutation intent (one action the customer has opened). Every attempt of
   * that intent sends the same key: double clicks, network failures, timeouts, and an adjusted
   * resubmission after a refusal (the backend never consumes a refused key). A new key starts only
   * after a success (the key is consumed) or once the intent is settled or abandoned.
   *
   * After an outcome-unknown failure the server may already have applied the key, so the request is
   * frozen: only the identical request may be retried under that key.
   */
  function createIntents(keyFactory) {
    let current = null;
    return {
      keyFor(kind) {
        if (!current || current.kind !== kind) current = { kind, key: keyFactory(), frozen: null };
        return current.key;
      },
      freeze(payload) { if (current) current.frozen = JSON.stringify(payload); },
      frozenPayload() { return current && current.frozen ? JSON.parse(current.frozen) : null; },
      settle() { current = null; }
    };
  }

  /**
   * POSTs one action. Result: { kind: 'ok', body } | { kind: 'refused', status, body } |
   * { kind: 'retry' } (network failure, timeout or 503: the outcome is unknown, retry with the same key).
   */
  async function postAction(url, payload, options = {}) {
    const fetchImpl = options.fetchImpl || (typeof fetch === 'function' ? fetch.bind(globalThis) : null);
    if (!fetchImpl) return { kind: 'retry' };
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), options.timeoutMs || 12000) : null;
    try {
      const response = await fetchImpl(url, {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
        signal: controller ? controller.signal : undefined
      });
      let body = null;
      try { body = await response.json(); } catch (error) { body = null; }
      if (response.status === 200 && isObject(body)) return { kind: 'ok', body };
      if (response.status === 503 || response.status >= 500 || !isObject(body)) return { kind: 'retry', status: response.status };
      return { kind: 'refused', status: response.status, body };
    } catch (error) {
      return { kind: 'retry' };
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  // ===== Secure payment (shared with My Shared Pours) =========================================

  const PAYMENT_ID = /^[A-Za-z0-9_-]{1,64}$/;
  const PAYMENT_BUSY_LABEL = 'Opening secure checkout…';
  const PAYMENT_DELAY_NOTE = 'Payment is confirmed once Shopify verifies your completed order. Paid already? It can take a few minutes to appear.';
  // Phase 1 (native checkout): the amount shown is the portion subtotal. Delivery and any voucher are
  // Shopify's to calculate and validate at checkout - never computed or promised here.
  const CHECKOUT_NOTE = 'Delivery is calculated at secure Shopify checkout, where an eligible Archive Rewards voucher can be applied, subject to its terms.';
  // The restricted production pilot (server field pilot: true): delivery and vouchers are off by default
  // and the API does not say which apply, so the pilot promises neither.
  const PILOT_CHECKOUT_NOTE = 'Review the final amount at secure Shopify checkout before you pay.';
  const checkoutNote = (pilot) => (pilot === true ? PILOT_CHECKOUT_NOTE : CHECKOUT_NOTE);
  const PAYMENT_COPY = {
    PaymentPreparing: 'Your payment link is being prepared — please try again in a few minutes.',
    PaymentProcessing: 'Your payment has been received and is being confirmed.',
    AllocationExpired: 'The payment window has closed.',
    AllocationAlreadyPaid: 'This allocation has already been paid.',
    UnresolvedPaymentIssues: 'The Archive is reviewing a payment for this allocation.',
    PaymentsUnavailable: 'Payment is temporarily unavailable. Please try again later.',
    TemporarilyUnavailable: 'Payment is temporarily unavailable. Please try again later.',
    NotFound: 'This allocation is no longer available.',
    SignInRequired: 'Your session has ended. Please sign in again to continue.',
    PilotPaused: PILOT_PAUSED
  };

  function paymentMessage(reasonCode) {
    return PAYMENT_COPY[reasonCode] || 'This payment could not be started. Please refresh and try again.';
  }

  /**
   * Asks the backend for the customer's live checkout for ONE allocation the server listed in
   * actions.pay.allocationIds. The body holds only the two ids: the server decides customer, amount
   * and package. Result:
   *   { kind: 'redirect', url }  - the caller navigates to url at once and never shows, stores,
   *                                logs or passes it on (it is a bearer link);
   *   { kind: 'refused', reasonCode, status, text, refetch } - refetch is false for "temporarily
   *                                unavailable" (no automatic retry, no polling loop).
   * Asked again on every click; nothing is cached.
   */
  async function requestPayment(apiBase, sharedPourId, allocationId, options = {}) {
    const refused = (reasonCode, status, refetch = true) => ({ kind: 'refused', reasonCode, status, text: paymentMessage(reasonCode), refetch });
    if (typeof sharedPourId !== 'string' || !PAYMENT_ID.test(sharedPourId) || typeof allocationId !== 'string' || !PAYMENT_ID.test(allocationId)) {
      return refused('ValidationFailed', 400, false);
    }
    const fetchImpl = options.fetchImpl || (typeof fetch === 'function' ? fetch.bind(globalThis) : null);
    if (!fetchImpl) return refused('TemporarilyUnavailable', 0, false);
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), options.timeoutMs || 12000) : null;
    try {
      const response = await fetchImpl(`${apiBase}/allocation/pay`, {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ sharedPourId, allocationId }),
        signal: controller ? controller.signal : undefined
      });
      let body = null;
      try { body = await response.json(); } catch (error) { body = null; }
      if (response.status === 200 && isObject(body) && typeof body.invoiceUrl === 'string' && /^https:\/\//i.test(body.invoiceUrl)) {
        return { kind: 'redirect', url: body.invoiceUrl };
      }
      const reasonCode = isObject(body) && typeof body.reasonCode === 'string'
        ? body.reasonCode
        : (response.status === 401 ? 'SignInRequired' : response.status >= 500 ? 'TemporarilyUnavailable' : 'InvalidState');
      return refused(reasonCode, response.status, response.status < 500 && reasonCode !== 'PaymentsUnavailable' && reasonCode !== 'TemporarilyUnavailable');
    } catch (error) {
      return refused('TemporarilyUnavailable', 0, false);
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  // ===== Rendering ==========================================================================

  const ICON_PREV = '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M12.5 4.5 7 10l5.5 5.5"/></svg>';
  const ICON_NEXT = '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M7.5 4.5 13 10l-5.5 5.5"/></svg>';
  const ICON_LOCK = '<svg viewBox="0 0 20 22" aria-hidden="true" focusable="false"><path d="M5 9.5V7a5 5 0 0 1 10 0v2.5"/><rect x="2.5" y="9.5" width="15" height="10.5" rx="1.5"/></svg>';
  const ICON_CHECK = '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="m5 10.5 3.2 3.2L15 6.8"/></svg>';
  const ICON_CLOCK = '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><circle cx="10" cy="10" r="7.6"/><path d="M10 5.8V10l2.8 1.9"/></svg>';
  const ICON_PEOPLE = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="9" cy="8" r="3"/><circle cx="17" cy="9" r="2.4"/><path d="M3.5 19c.6-3.2 2.8-5 5.5-5s4.9 1.8 5.5 5M14.5 14.6c.8-.4 1.6-.6 2.5-.6 2.2 0 3.9 1.4 4.4 4"/></svg>';
  const ICON_PERSON = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="8" r="3.6"/><path d="M4.8 20c.7-3.9 3.6-6.2 7.2-6.2s6.5 2.3 7.2 6.2"/></svg>';
  const ICON_CART = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M2.5 4h2.4l2.3 10.2a1.5 1.5 0 0 0 1.5 1.2h8.6a1.5 1.5 0 0 0 1.5-1.1L21 8H6.1"/><circle cx="9.5" cy="19.5" r="1.4"/><circle cx="17" cy="19.5" r="1.4"/></svg>';
  const ICON_CLOSE = '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M5 5l10 10M15 5 5 15"/></svg>';

  function renderImage(sources, className, sizes) {
    if (!sources) return '';
    const srcset = sources.srcset ? ` srcset="${escapeHtml(sources.srcset)}" sizes="${escapeHtml(sizes)}"` : '';
    // Decorative: the option's name is the card heading, so the image adds no new information.
    return `<img class="${className}" src="${escapeHtml(sources.src)}"${srcset} alt="" loading="lazy" decoding="async">`;
  }

  const PROGRESS_UNAVAILABLE = '<p class="asp-progress__text asp-progress__text--unavailable">Progress unavailable</p>';

  // `text` is the full wording for assistive technology; `shown` is an optional compact form.
  function renderProgress(percent, now, max, label, text, sub, shown = text) {
    return `
      <div class="asp-progress">
        <div class="asp-progress__bar" role="progressbar" aria-label="${escapeHtml(label)}" aria-valuemin="0" aria-valuemax="${max}" aria-valuenow="${now}" aria-valuetext="${escapeHtml(text)}">
          <span class="asp-progress__fill" style="width: ${percent}%"></span>
        </div>
        <p class="asp-progress__text">${escapeHtml(shown)}</p>
        ${sub ? `<p class="asp-progress__sub">${escapeHtml(sub)}</p>` : ''}
      </div>`;
  }

  function renderStepper(id, name, value, bounds, scope, locked = false) {
    return `
      <div class="asp-stepper" role="group" aria-label="Portions of ${escapeHtml(name)}">
        <button type="button" class="asp-stepper__btn" data-asp-step="-1" data-asp-scope="${scope}" data-package="${escapeHtml(id)}" aria-label="One fewer portion of ${escapeHtml(name)}"${locked || value <= bounds.min ? ' disabled' : ''}>−</button>
        <output class="asp-stepper__value" data-asp-qty="${escapeHtml(id)}" data-asp-scope="${scope}" aria-live="polite">${value}</output>
        <button type="button" class="asp-stepper__btn" data-asp-step="1" data-asp-scope="${scope}" data-package="${escapeHtml(id)}" aria-label="One more portion of ${escapeHtml(name)}"${locked || value >= bounds.max ? ' disabled' : ''}>+</button>
      </div>`;
  }

  // Card anatomy follows the prototype: image window, name, structure, price, progress, own state.
  function renderCard(pkg, vm, ui = {}) {
    const mine = pkg.mine;
    const quantity = (ui.selection && ui.selection[pkg.id]) || 0;
    const conflict = (ui.conflicts || []).includes(pkg.id);
    let controls = '';
    if (vm.canSelect) {
      const bounds = selectBounds(pkg);
      controls = bounds.max > 0
        ? `
          <div class="asp-select" data-asp-select="${escapeHtml(pkg.id)}">
            ${renderStepper(pkg.id, pkg.name, quantity, bounds, 'select')}
            <p class="asp-select__limit">Max ${pkg.limit.currentQuantity + pkg.limit.maxAdditional} per member</p>          </div>`
        : '<p class="asp-select__limit asp-select__limit--none">Not available to add</p>';
    } else if (vm.canManage) {
      controls = `<button type="button" class="asp-card__link" data-asp-open="manage">${mine ? 'Adjust quantity' : 'Add portions'}</button>`;
    }
    return `
      <li class="asp-card${mine ? ' asp-card--mine' : ''}${quantity > 0 ? ' asp-card--selected' : ''}${conflict ? ' asp-card--conflict' : ''}" data-asp-card="${escapeHtml(pkg.id)}">
        <div class="asp-card__media${pkg.image ? '' : ' asp-card__media--none'}">${renderImage(pkg.image, 'asp-card__image', '(min-width: 1600px) 240px, (min-width: 750px) 180px, 80vw')}</div>
        <div class="asp-card__body">
          <div class="asp-card__head">
            ${pkg.stateBadge
              ? `<p class="asp-badge">${escapeHtml(pkg.stateBadge)}</p>`
              : (vm.packages.some((other) => other.stateBadge) ? '<p class="asp-badge asp-badge--slot" aria-hidden="true">&nbsp;</p>' : '')}
            <h3 class="asp-card__name">${escapeHtml(pkg.name)}</h3>
            <p class="asp-card__structure">${escapeHtml(pkg.structure)}</p>
            <p class="asp-card__price">${escapeHtml(pkg.priceText)}</p>
            <p class="asp-card__unit">per portion</p>
          </div>
          ${pkg.progressAvailable
            ? renderProgress(pkg.percent, pkg.committed, pkg.target, `${pkg.name}: portions committed`, pkg.committedText, pkg.remainingText, `${pkg.committed} / ${pkg.target} portions committed`)
            : PROGRESS_UNAVAILABLE}
          ${mine || controls ? `
            <div class="asp-card__foot">
              ${mine ? `
                <div class="asp-mine">
                  <span class="asp-mine__icon">${ICON_CHECK}</span>
                  <div class="asp-mine__text">
                    <p class="asp-mine__label">Your commitment</p>
                    <p class="asp-mine__qty">${escapeHtml(mine.quantityText)}</p>
                    ${mine.positionsText ? `<p class="asp-mine__meta">Your ${escapeHtml(mine.positionsText.toLowerCase())}${mine.provisional ? ' <span class="asp-mine__provisional">(provisional)</span>' : ''}</p>` : ''}
                    ${mine.reserveText ? `<p class="asp-mine__meta">${escapeHtml(mine.reserveText)}</p>` : ''}
                  </div>
                </div>` : ''}
              ${controls}
            </div>` : ''}
        </div>
      </li>`;
  }

  function renderCarousel(vm, ids, ui) {
    const modifier = vm.packageCount === 1 ? ' asp-carousel__track--single' : ' asp-carousel__track--multi';
    return `
      <div class="asp-carousel" data-asp-carousel>
        <div class="asp-carousel__stage">
          <div class="asp-carousel__viewport" id="${ids.viewport}" data-asp-viewport role="region" aria-label="Shared Pour options">
            <ul class="asp-carousel__track${modifier}" role="list" style="--asp-count: ${vm.packageCount}">
              ${vm.packages.map((pkg) => renderCard(pkg, vm, ui)).join('')}
            </ul>
          </div>
          <button type="button" class="asp-carousel__arrow asp-carousel__arrow--prev" data-asp-prev hidden aria-controls="${ids.viewport}" aria-label="Previous options">${ICON_PREV}</button>
          <button type="button" class="asp-carousel__arrow asp-carousel__arrow--next" data-asp-next hidden aria-controls="${ids.viewport}" aria-label="Next options">${ICON_NEXT}</button>
        </div>
        <div class="asp-carousel__nav" data-asp-nav hidden>
          <span aria-hidden="true"></span>
          <div class="asp-carousel__dots" data-asp-dots></div>
          <p class="asp-carousel__status" data-asp-status aria-live="polite"></p>
        </div>
      </div>`;
  }

  function renderStage(stage) {
    if (!stage) return '';
    const current = stage.labels[stage.index];
    return `
      <div class="asp-stages">
        <p class="asp-stages__current" aria-hidden="true">Stage ${stage.index + 1} of ${stage.labels.length} · ${escapeHtml(current)}</p>
        <ol class="asp-stages__list" aria-label="Shared Pour progress">
          ${stage.labels.map((label, index) => `
            <li class="asp-stages__step${index < stage.index ? ' is-done' : ''}${index === stage.index ? ' is-current' : ''}"${index === stage.index ? ' aria-current="step"' : ''}>
              <span class="asp-stages__bar" aria-hidden="true"></span>
              <span class="asp-stages__label">${escapeHtml(label)}${index < stage.index ? '<span class="visually-hidden"> (complete)</span>' : ''}</span>
            </li>`).join('')}
        </ol>
      </div>`;
  }

  function renderYou(you) {
    if (!you) return '';
    const icon = you.tone === 'success' ? ICON_CHECK : ICON_CLOCK;
    return `
          <div class="asp-you asp-you--${you.tone}" role="status">
            <span class="asp-you__icon" aria-hidden="true">${icon}</span>
            <p class="asp-you__title">${escapeHtml(you.title)}</p>
            <p class="asp-you__text">${escapeHtml(you.text)}</p>
          </div>`;
  }

  function renderWinner(vm) {
    if (!vm.winner) return '';
    const winner = vm.winner;
    const progress = vm.winnerProgress;
    return `
      <div class="asp-winner${vm.you ? ' asp-winner--yours' : ''}">
        <div class="asp-winner__media${winner.thumb ? '' : ' asp-winner__media--none'}">${renderImage(winner.thumb, 'asp-winner__image', '96px')}</div>
        <div class="asp-winner__body">
          <div class="asp-winner__heading">
            <h3 class="asp-winner__name">${escapeHtml(winner.name)}</h3>
            <p class="asp-badge asp-badge--gold">${vm.publicState === 'Finalising' ? 'Filled' : 'Selected option'}</p>
          </div>
          <p class="asp-winner__meta">${escapeHtml(winner.structure)} · ${escapeHtml(winner.priceText)} per portion</p>
          ${progress ? renderProgress(progress.percent, progress.now, progress.max, progress.label, progress.text, progress.sub) : ''}
          ${renderYou(vm.you)}
        </div>
      </div>`;
  }

  /** Read-only rows: each other option's name, its badge and the commitment it reached. */
  function renderResults(vm) {
    const results = vm.results;
    if (!results) return '';
    return `
      <section class="asp-results" aria-label="${escapeHtml(results.heading)}">
        <div class="asp-results__top">
          <p class="asp-results__heading">${escapeHtml(results.heading)}</p>
          ${results.note ? `<p class="asp-results__note">${escapeHtml(results.note)}</p>` : ''}
        </div>
        <ul class="asp-results__list" role="list">
          ${results.rows.map((row) => `
            <li class="asp-result" data-asp-result="${escapeHtml(row.id)}">
              <div class="asp-result__head">
                <h4 class="asp-result__name">${escapeHtml(row.name)}</h4>
                ${row.badge ? `<p class="asp-badge">${escapeHtml(row.badge)}</p>` : ''}
              </div>
              <div class="asp-result__progress">
                ${row.progress ? renderProgress(row.progress.percent, row.progress.now, row.progress.max, row.progress.label, row.progress.text, '', row.progress.shown) : PROGRESS_UNAVAILABLE}
                ${row.yours ? `<p class="asp-result__yours">${escapeHtml(row.yours)}</p>` : ''}
              </div>
            </li>`).join('')}
        </ul>
      </section>`;
  }

  function renderPostActions(vm, ui = {}) {
    const a = vm.postActions;
    const buttons = [];
    if (a.pay) buttons.push(...renderPayButtons(a.pay, ui));
    // The same server action claims portions free right now (claimsAvailablePortions), or - on an
    // older backend - commits to available portions while a recovery window is open.
    const joinLabel = vm.claim ? 'Claim available portions'
      : vm.recovery ? (vm.recoveryTopUp ? 'Add portions' : 'Commit to available portions')
        : (vm.actions.withdrawReserve.portions > 0 ? 'Add reserve portions' : 'Join the reserve queue');
    if (a.joinReserve) buttons.push(`<button type="button" class="asp-btn asp-btn--primary asp-panel__action" data-asp-open="join-reserve">${joinLabel}</button>`);
    if (a.withdrawReserve) buttons.push('<button type="button" class="asp-panel__link asp-panel__link--destructive" data-asp-open="withdraw-reserve">Leave the reserve queue</button>');
    if (a.decline) buttons.push('<button type="button" class="asp-panel__link asp-panel__link--destructive" data-asp-open="decline">Decline allocation</button>');
    // Participants: the window in one line (a visitor's recovery status already says it).
    const name = vm.winner ? vm.winner.name : '';
    const recoveryNote = a.joinReserve && vm.claim && !vm.recoveryJoin && vm.claim.available > 0
      ? `<p class="asp-panel__note asp-recovery__note">${escapeHtml(`${claimCount(vm.claim, name)} ${vm.claim.available === 1 ? 'is' : 'are'} available ${vm.claim.closesText ? `until ${vm.claim.closesText}` : 'to claim now'}.`)}</p>`
      : a.joinReserve && vm.recovery && !vm.claim && !vm.recoveryJoin
        ? `<p class="asp-panel__note asp-recovery__note">${escapeHtml(`${recoveryCount(vm.recovery, name)} ${vm.recovery.available === 1 ? 'is' : 'are'} available until ${vm.recovery.closesText}.`)}</p>`
        : '';
    return buttons.length
      ? `${recoveryNote}<div class="asp-panel__actions">${buttons.join('')}</div>${a.pay ? `<p class="asp-panel__note asp-pay__note">${escapeHtml(PAYMENT_DELAY_NOTE)}</p>` : ''}`
      : '';
  }

  /**
   * One "Complete payment" button per allocation id the server allows (labelled with the server's
   * total when there is more than one). data-asp-pay is the position in that server list; the
   * allocation id itself stays in memory and never reaches the page.
   */
  function renderPayButtons(pay, ui = {}) {
    const many = pay.allocations.length > 1;
    return pay.allocations.map((allocation, index) => {
      const busy = ui.paying === allocation.id;
      const label = busy ? PAYMENT_BUSY_LABEL : `Complete payment${many && allocation.total ? ` · ${formatMoney(allocation.total)}` : ''}`;
      return `<button type="button" class="asp-btn asp-btn--primary asp-panel__action" data-asp-pay="${index}"${ui.paying ? ' disabled' : ''}${busy ? ' aria-busy="true"' : ''}>${escapeHtml(label)}</button>`;
    });
  }

  function renderStatus(vm, ui = {}) {
    const status = vm.status;
    if (!status) return '';
    return `
      <div class="asp-panel asp-panel--${status.tone}">
        <p class="asp-panel__eyebrow">${escapeHtml(status.eyebrow)}</p>
        <h3 class="asp-panel__title">${escapeHtml(status.title)}</h3>
        ${status.body ? `<p class="asp-panel__body">${escapeHtml(status.body)}</p>` : ''}
        ${status.lines.length ? `
          <dl class="asp-panel__lines">
            ${status.lines.map((line) => `
              <div class="asp-panel__line">
                <dt>${escapeHtml(line.label)}</dt>
                <dd><span class="asp-panel__value">${escapeHtml(line.value)}</span>${line.sub ? `<span class="asp-panel__sub">${escapeHtml(line.sub)}</span>` : ''}</dd>
              </div>`).join('')}
          </dl>` : ''}
        ${renderPostActions(vm, ui)}
      </div>`;
  }

  const SELECTION_NONE = 'Choose portions in one or more pour options. No payment is taken when you commit.';
  const SELECTION_NOTE = 'Only the option that fills can become payable. The Archive confirms your potential commitment when you commit.';

  function renderSelection(vm, ui = {}) {
    const count = selectionCount(vm, ui.selection || {});
    return `
      <div class="asp-panel asp-panel--select" data-asp-selection>
        <div class="asp-select-summary">
          <p class="asp-panel__eyebrow">Your selection</p>
          <p class="asp-select-summary__count" data-asp-selection-count>${count.portions ? escapeHtml(selectionCountText(count)) : 'Nothing chosen yet'}</p>
          <p class="asp-panel__note" data-asp-selection-note>${count.portions ? SELECTION_NOTE : SELECTION_NONE}</p>
        </div>
        <button type="button" class="asp-btn asp-btn--secondary asp-panel__cta" data-asp-open="review"${count.portions ? '' : ' disabled'}>Review commitment</button>
      </div>`;
  }

  /**
   * "View My Shared Pours" - the primary action once commitments are confirmed.
   *
   * AWAITING THE MY SHARED POURS PAGE: there is no destination yet. When the block supplies
   * data-my-shared-pours-url (blocks/archive-shared-pour.liquid) this becomes a real link. Until
   * then it is a button marked aria-disabled that goes nowhere and, when pressed, says the page is
   * coming soon, so nobody is sent to a page that does not exist. It never calls the backend.
   */
  function renderMySharedPoursAction(vm) {
    const label = 'View My Shared Pours';
    if (vm.mySharedPoursUrl) {
      return `<a class="asp-btn asp-btn--nav asp-foot__action" href="${escapeHtml(vm.mySharedPoursUrl)}">${label}<span aria-hidden="true"> →</span></a>`;
    }
    return `<button type="button" class="asp-btn asp-btn--nav asp-foot__action" data-asp-pending="my-shared-pours" aria-disabled="true">${label}<span aria-hidden="true"> →</span></button>`;
  }

  /**
   * The panel's last line, in every layout: Shared Pour terms on the left and, for any signed-in
   * customer (never a guest), "View My Shared Pours" on the right - its one place in the panel.
   * A guest's banner has no header, so "How Shared Pour works" (the same dialog the full panel's
   * header opens) sits beside the terms instead (ids given only by the banner).
   */
  function renderFoot(vm, ids) {
    let terms = vm.termsUrl ? `<a class="asp__terms" href="${escapeHtml(vm.termsUrl)}">Shared Pour terms</a>` : '';
    if (ids && !vm.signedIn) {
      const how = `<button type="button" class="asp-foot__how" data-asp-open-how aria-haspopup="dialog" aria-controls="${ids.dialog}">${INTRO.how}</button>`;
      terms = `<span class="asp-foot__links">${terms}${how}</span>`;
    }
    const action = vm.signedIn ? renderMySharedPoursAction(vm) : '';
    if (!terms && !action) return '';
    return `
          <div class="asp-foot">
            ${terms || '<span></span>'}
            ${action}
          </div>`;
  }

  /** The compact banner for visitors without commitments. */
  function renderBanner(vm, ids, ui = {}) {
    const banner = vm.banner;
    if (!banner) return '';
    return `
      <div class="asp asp--banner asp-interest">
        <div class="asp-interest__top">
          <h2 class="asp-interest__brand" id="${ids.heading}">${INTRO.heading}</h2>
          <p class="asp-interest__badge"><span class="asp-interest__badge-icon" aria-hidden="true">${ICON_PEOPLE}</span>${escapeHtml(banner.badge)}</p>
        </div>
        ${ui.notice ? renderNotice(ui) : ''}
        <div class="asp-interest__body">
          <img class="asp-interest__icon" src="${INTRO.iconUrl}" alt="" width="44" height="44" decoding="async">
          <div class="asp-interest__copy">
            <p class="asp-interest__headline">${escapeHtml(banner.headline)}${banner.tagline ? ` <em class="asp-interest__tagline">${escapeHtml(banner.tagline)}</em>` : ''}</p>
            ${banner.lines.map((line) => `<p class="asp-interest__text">${line.strong ? `<strong>${escapeHtml(line.strong)}</strong> ` : ''}${escapeHtml(line.text)}</p>`).join('')}
          </div>
        </div>
        ${banner.invite ? `
          <div class="asp-interest__invite">
            <span class="asp-interest__invite-icon" aria-hidden="true">${ICON_CART}</span>
            <p class="asp-interest__invite-title">${escapeHtml(banner.invite.title)}</p>
            <p class="asp-interest__invite-text">${escapeHtml(banner.invite.text)}</p>
          </div>` : ''}
        ${banner.signIn ? `
          <div class="asp-interest__signin">
            <span class="asp-interest__signin-icon" aria-hidden="true">${ICON_PERSON}</span>
            <div class="asp-interest__signin-copy">
              <p class="asp-interest__signin-title">${escapeHtml(banner.signIn.title)}</p>
              <p class="asp-interest__signin-text">${escapeHtml(banner.signIn.text)}</p>
            </div>
            <a class="asp-btn asp-btn--nav asp-interest__signin-action" href="${escapeHtml(vm.loginUrl)}">${escapeHtml(banner.signIn.label)}<span aria-hidden="true"> →</span></a>
          </div>` : ''}
        ${renderFoot(vm, ids)}
      </div>`;
  }

  /** The confirmed-commitment panel. Every value shown is the server's (vm.footer). */
  function renderConfirmed(vm, footer) {
    return `
      <div class="asp-panel asp-panel--success asp-panel--confirmed">
        <div class="asp-confirmed__head">
          <span class="asp-confirmed__icon" aria-hidden="true">${ICON_CHECK}</span>
          <div class="asp-confirmed__heading">
            <h3 class="asp-panel__title asp-confirmed__title">Your commitments are confirmed.</h3>
            <p class="asp-confirmed__summary">${escapeHtml(footer.countText)}</p>
          </div>
        </div>
        ${footer.rangeText ? `
          <div class="asp-confirmed__potential">
            <p class="asp-confirmed__label">Potential commitment</p>
            <p class="asp-confirmed__amount">${escapeHtml(footer.rangeText)}</p>
            ${footer.isRange ? '<p class="asp-confirmed__qualifier">Depending on which option fills; this is not a combined total.</p>' : ''}
          </div>` : ''}
        <p class="asp-confirmed__note">No payment has been taken. You’ll only be invited to pay if an option you chose fills.</p>
        ${footer.paused ? `<p class="asp-confirmed__note">${escapeHtml(PILOT_PAUSED)}</p>` : ''}
        ${vm.canManage ? `
          <div class="asp-confirmed__actions">
            <button type="button" class="asp-btn asp-btn--secondary asp-confirmed__action" data-asp-open="manage">Manage commitments</button>
          </div>` : ''}
        <p class="asp-confirmed__pending" data-asp-pending-note role="status" aria-live="polite" hidden></p>
      </div>`;
  }

  const UNAVAILABLE_COPY = {
    NotOpenYet: 'Commitments open soon.',
    CommitmentsClosed: 'Commitments for this Shared Pour have closed.',
    EventFinalising: 'An option has filled; commitments have closed.',
    AtEventCap: 'You have reached the maximum portions per member for this Shared Pour.',
    PilotPaused: PILOT_PAUSED
  };

  function renderFooter(vm, ui) {
    const footer = vm.footer;
    if (!footer) return '';
    if (footer.kind === 'gate') {
      return `
        <div class="asp-panel asp-panel--gate">
          <div class="asp-panel__row">
            <span class="asp-panel__icon">${ICON_LOCK}</span>
            <div class="asp-panel__content">
              <h3 class="asp-panel__title">Sign in to make a commitment.</h3>
              <p class="asp-panel__body">${vm.publicState === 'Upcoming' ? 'Sign in to be ready when commitments open. ' : ''}Choose one or more pour options and the number of portions. No payment is taken when you commit.</p>
            </div>
          </div>
          <a class="asp-btn asp-btn--primary asp-panel__cta" href="${escapeHtml(vm.loginUrl)}">Sign in to join</a>
        </div>`;
    }
    if (footer.kind === 'select') return renderSelection(vm, ui);
    if (footer.kind === 'participant') return renderConfirmed(vm, footer);
    return `
      <div class="asp-panel asp-panel--neutral">
        <p class="asp-panel__body">${escapeHtml(UNAVAILABLE_COPY[footer.reasonCode] || 'Commitments aren’t available right now.')}</p>
      </div>`;
  }

  function renderNotice(ui) {
    if (!ui || !ui.notice) return '<p class="asp__notice" data-asp-notice role="status" aria-live="polite" hidden></p>';
    return `<p class="asp__notice asp__notice--${ui.notice.tone}" data-asp-notice role="status" aria-live="polite">${escapeHtml(ui.notice.text)}</p>`;
  }

  function renderComponent(vm, ids, ui = {}) {
    if (vm.layout === 'hidden') return '';
    // A notice reaches the banner too: a visitor whose commitment was refused (e.g. the recovery
    // window just closed) lands back on it and still needs to know why.
    if (vm.layout === 'banner' && vm.banner) return renderBanner(vm, ids, ui);
    return `
      <div class="asp">
        <header class="asp__header">
          <div class="asp__heading-row">
            <h2 class="asp__title" id="${ids.heading}">${INTRO.heading}</h2>
            <button type="button" class="asp__how" data-asp-open-how aria-haspopup="dialog" aria-controls="${ids.dialog}">${INTRO.how} <span aria-hidden="true">→</span></button>
          </div>
          <div class="asp__intro">
            <img class="asp__intro-icon" src="${INTRO.iconUrl}" alt="" width="48" height="48" decoding="async">
            <div class="asp__intro-text">
              <p class="asp__tagline">${escapeHtml(INTRO.tagline)}</p>
              <p class="asp__explanation">${escapeHtml(INTRO.explanation)}</p>
            </div>
          </div>
          <p class="asp__lede">${escapeHtml(vm.lede)}</p>
          ${vm.timelineNote ? `<p class="asp__timeline">${escapeHtml(vm.timelineNote)}</p>` : ''}
        </header>
        ${renderNotice(ui)}
        ${vm.phase === 'selecting' ? renderCarousel(vm, ids, ui) : renderStage(vm.stage) + renderWinner(vm) + renderResults(vm)}
        ${vm.phase === 'selecting' ? renderFooter(vm, ui) : renderStatus(vm, ui)}
        ${vm.showDisclosure ? `
          <div class="asp__footnote">
            <p>${escapeHtml(DISCLOSURE)}</p>
          </div>` : ''}
        ${renderFoot(vm)}
      </div>`;
  }

  // ----- Dialogs ------------------------------------------------------------------------------

  function renderHowItWorks(vm, ids) {
    return `
      <dialog class="asp-dialog" id="${ids.dialog}" data-asp-dialog aria-labelledby="${ids.dialogTitle}">
        <div class="asp-dialog__inner">
          <div class="asp-dialog__header">
            <h2 class="asp-dialog__title" id="${ids.dialogTitle}">How Archive Shared Pour works</h2>
            <button type="button" class="asp-dialog__close" data-asp-close aria-label="Close">${ICON_CLOSE}</button>
          </div>
          ${vm.descriptionParagraphs.length ? `<div class="asp-dialog__intro">${vm.descriptionParagraphs.map((text) => `<p>${escapeHtml(text)}</p>`).join('')}</div>` : ''}
          <ol class="asp-dialog__steps">
            ${HOW_IT_WORKS.map(([title, body], index) => `
              <li class="asp-dialog__step">
                <span class="asp-dialog__number" aria-hidden="true">${index + 1}</span>
                <div><h3 class="asp-dialog__step-title">${escapeHtml(title)}</h3><p>${escapeHtml(body)}</p></div>
              </li>`).join('')}
          </ol>
          <p class="asp-dialog__note"><strong>Please note:</strong> ${escapeHtml(HOW_IT_WORKS_NOTE)}</p>
        </div>
        <p class="asp-dialog__more" aria-hidden="true">Scroll for more <span class="asp-dialog__more-arrow">↓</span></p>
      </dialog>`;
  }

  /**
   * The How it works list is often taller than the screen, and step 7 can end exactly at the
   * modal's lower edge, so it looks finished while the note below it is hidden (overlay scrollbars
   * on macOS, iOS and Android show nothing). While more lies below, the dialog carries
   * data-asp-more: the text fades out under a quiet "Scroll for more" (CSS). Visual only - the
   * note is in the dialog for screen readers either way.
   */
  function updateScrollCue(dialog) {
    const body = dialog && dialog.querySelector('.asp-dialog__inner');
    if (!body) return;
    const more = body.scrollHeight - body.clientHeight - body.scrollTop > 8;
    if (more) dialog.setAttribute('data-asp-more', '');
    else dialog.removeAttribute('data-asp-more');
    // Keep the fade clear of a classic (non-overlay) scrollbar.
    if (dialog.style && typeof dialog.style.setProperty === 'function') {
      dialog.style.setProperty('--asp-scrollbar', `${Math.max(0, body.offsetWidth - body.clientWidth)}px`);
    }
  }

  function renderModalFrame(ids, title, body, footer, ui, actionsClass = '') {
    return `
      <div class="asp-dialog__header">
        <h2 class="asp-dialog__title" id="${ids.actionTitle}">${escapeHtml(title)}</h2>
        <button type="button" class="asp-dialog__close" data-asp-close aria-label="Close"${ui.busy ? ' disabled' : ''}>${ICON_CLOSE}</button>
      </div>
      ${body}
      ${ui.error ? `<div class="asp-alert" role="alert">${escapeHtml(ui.error.text)}${ui.error.signIn ? ` <a href="${escapeHtml(ui.error.signIn)}">Sign in</a>` : ''}</div>` : ''}
      <div class="asp-dialog__actions${actionsClass ? ` ${actionsClass}` : ''}">${footer}</div>`;
  }

  // Irreversible or giving-up actions take the restrained destructive treatment, never the primary one.
  const DESTRUCTIVE_ACTIONS = ['decline', 'withdraw-reserve'];

  function primaryButton(label, busyLabel, action, ui, disabled = false) {
    return `<button type="button" class="asp-btn ${DESTRUCTIVE_ACTIONS.includes(action) ? 'asp-btn--destructive' : 'asp-btn--primary'} asp-dialog__primary" data-asp-submit="${action}"${ui.busy || disabled ? ' disabled' : ''}${ui.busy ? ' aria-busy="true"' : ''}>${escapeHtml(ui.busy ? busyLabel : (ui.error && ui.error.retry ? 'Try again' : label))}</button>`;
  }

  function cancelButton(ui) {
    return `<button type="button" class="asp-btn asp-btn--quiet asp-dialog__secondary" data-asp-close${ui.busy ? ' disabled' : ''}>Cancel</button>`;
  }

  function renderReview(vm, ids, ui) {
    const rows = vm.packages.filter((pkg) => (ui.selection[pkg.id] || 0) > 0).map((pkg) => {
      const quantity = ui.selection[pkg.id];
      const conflict = (ui.conflicts || []).includes(pkg.id);
      return `
        <li class="asp-review__row${conflict ? ' asp-review__row--conflict' : ''}">
          <div><p class="asp-review__name">${escapeHtml(pkg.name)}</p><p class="asp-review__meta">${escapeHtml(pkg.structure)}</p></div>
          <p class="asp-review__qty">${escapeHtml(plural(quantity, 'portion'))} × ${escapeHtml(pkg.priceText)}</p>
        </li>`;
    }).join('');
    const body = `
      <ul class="asp-review" role="list">${rows}</ul>
      <ul class="asp-notes" role="list">
        <li>Only one option can fill. You’ll only be invited to pay for your portions in the option that fills — these amounts are not added together.</li>
        <li>No payment is taken now.</li>
        <li>A commitment does not reserve the bottle. It may still sell through another Archive channel before the Shared Pour secures it; if it does, nobody is charged.</li>
        <li>Your place in the queue and your potential commitment are confirmed by the Archive when you confirm.</li>
      </ul>
      ${vm.termsUrl ? `<p class="asp-dialog__fine">By confirming you agree to the <a href="${escapeHtml(vm.termsUrl)}">Shared Pour terms</a>.</p>` : ''}`;
    return renderModalFrame(ids, 'Confirm your commitment', body, cancelButton(ui) + primaryButton('Confirm commitment', 'Confirming…', 'confirm', ui), ui);
  }

  function renderManage(vm, ids, ui) {
    const rows = vm.packages.map((pkg) => {
      const bounds = manageBounds(vm, pkg);
      const value = ui.drafts[pkg.id];
      const conflict = (ui.conflicts || []).includes(pkg.id);
      const mine = pkg.mine;
      return `
        <li class="asp-manage__row${conflict ? ' asp-review__row--conflict' : ''}">
          <div class="asp-manage__media${pkg.thumb ? '' : ' asp-manage__media--none'}">${renderImage(pkg.thumb, 'asp-manage__image', '88px')}</div>
          <div class="asp-manage__info">
            <p class="asp-review__name">${escapeHtml(pkg.name)}</p>
            <p class="asp-review__meta">${escapeHtml(pkg.structure)} · ${escapeHtml(pkg.priceText)} per portion</p>
            <p class="asp-review__meta">${escapeHtml(pkg.progressAvailable ? `${pkg.committed} / ${pkg.target} portions committed` : 'Progress unavailable')}</p>
            ${mine ? `<p class="asp-manage__current">Your commitment: ${escapeHtml(mine.quantityText)}${mine.positionsText ? ` · ${escapeHtml(mine.positionsText)}` : ''}</p>` : ''}
          </div>
          <div class="asp-manage__control">
            ${bounds.min === bounds.max ? `<p class="asp-manage__fixed">${value}</p>` : renderStepper(pkg.id, pkg.name, value, bounds, 'manage', ui.uncertain)}
            <p class="asp-select__limit">${bounds.max > bounds.current ? `Max ${bounds.max}` : 'No more available'}</p>
          </div>
        </li>`;
    }).join('');
    const changed = vm.packages.some((pkg) => ui.drafts[pkg.id] !== pkg.currentQuantity);
    const cutoff = vm.actions.decreaseCutoffUtc ? formatDateTime(vm.actions.decreaseCutoffUtc, ui.timeZone) : '';
    const current = vm.footer && vm.footer.kind === 'participant' ? vm.footer : null;
    const body = `
      <p class="asp-dialog__lede">Adjust the portions you’d like in each option. Your position updates from live commitments.</p>
      <ul class="asp-manage" role="list">${rows}</ul>
      ${current && current.rangeText ? `
        <div class="asp-review__total">
          <p class="asp-panel__eyebrow">Current potential commitment</p>
          <p class="asp-select-summary__range">${escapeHtml(current.rangeText)}</p>
          <p class="asp-panel__note">The Archive updates this when you save.</p>
        </div>` : ''}
      <ul class="asp-notes" role="list">
        <li>Added portions join the back of the queue. Reducing removes your newest portions first, so your earliest places are kept.</li>
        <li>Only one option can fill. You’ll only be invited to pay if an option you chose fills; no payment is taken now.</li>
        ${cutoff && (vm.actions.canDecrease || vm.actions.canCancel) ? `<li>Reductions are possible until ${escapeHtml(cutoff)}.</li>` : ''}
        ${!vm.actions.canDecrease && !vm.actions.canCancel ? '<li>Commitments can no longer be reduced.</li>' : ''}
      </ul>`;
    return renderModalFrame(ids, 'Manage your commitments', body, cancelButton(ui) + primaryButton('Save changes', 'Saving…', 'change', ui, !changed), ui);
  }

  function renderJoinReserve(vm, ids, ui) {
    const winner = vm.winner;
    const bounds = joinBounds(vm);
    if (vm.claim) {
      // Claiming portions free right now: an allocation at once, within the server's min and max.
      const claim = vm.claim;
      const lede = claim.available > 0
        ? `${claimCount(claim, winner ? winner.name : '')} ${claim.available === 1 ? 'is' : 'are'} available now.${claim.closesText ? ` You can claim them until ${claim.closesText}.` : ''}`
        : `Portions are available now.${claim.closesText ? ` You can claim them until ${claim.closesText}.` : ''}`;
      const claimBody = `
      <p class="asp-dialog__lede">${escapeHtml(lede)}</p>
      <div class="asp-manage__row asp-manage__row--single">
        <div class="asp-manage__info"><p class="asp-review__name">Portions</p><p class="asp-review__meta">${winner ? `${escapeHtml(winner.priceText)} per portion` : ''}</p></div>
        <div class="asp-manage__control">${renderStepper('reserve', winner ? winner.name : 'this pour option', ui.reserveQuantity, bounds, 'reserve', ui.uncertain)}<p class="asp-select__limit">${escapeHtml(bounds.min > 1 ? `Min ${bounds.min} · Max ${bounds.max}` : `Max ${bounds.max}`)}</p></div>
      </div>
      <ul class="asp-notes" role="list">
        <li>${escapeHtml(CLAIM_NOTE)}</li>
        <li>${escapeHtml(checkoutNote(vm.pilot))}</li>
        <li>Nothing is charged until you pay.</li>
      </ul>`;
      return renderModalFrame(ids, 'Claim available portions', claimBody, cancelButton(ui) + primaryButton('Claim portions', 'Claiming…', 'join-reserve', ui), ui);
    }
    if (vm.recovery) {
      // Recovery window: the same join request, for available portions only (nothing is queued). The
      // maximum is the server's maxPortions (available portions within this customer's limits).
      const title = vm.recoveryTopUp ? 'Add portions' : 'Commit to available portions';
      const recoveryBody = `
      <p class="asp-dialog__lede">${escapeHtml(`${recoveryCount(vm.recovery, winner ? winner.name : '')} ${vm.recovery.available === 1 ? 'has' : 'have'} become available. Commitments are open until ${vm.recovery.closesText}.`)}</p>
      <div class="asp-manage__row asp-manage__row--single">
        <div class="asp-manage__info"><p class="asp-review__name">Portions</p><p class="asp-review__meta">${winner ? `${escapeHtml(winner.priceText)} per portion` : ''}</p></div>
        <div class="asp-manage__control">${renderStepper('reserve', winner ? winner.name : 'this pour option', ui.reserveQuantity, bounds, 'reserve', ui.uncertain)}<p class="asp-select__limit">Max ${bounds.max}</p></div>
      </div>
      <ul class="asp-notes" role="list">
        <li>Available portions are allocated in commitment order. Once your portions are allocated, you’re invited to pay through secure Shopify checkout before the deadline shown.</li>
        <li>${escapeHtml(checkoutNote(vm.pilot))}</li>
        <li>Nothing is charged until you pay.</li>
      </ul>`;
      return renderModalFrame(ids, title, recoveryBody, cancelButton(ui) + primaryButton(vm.recoveryTopUp ? 'Add portions' : 'Commit', 'Committing…', 'join-reserve', ui), ui);
    }
    const body = `
      <p class="asp-dialog__lede">Join the ${escapeHtml(winner ? winner.name : '')} reserve queue. This is a place in the queue, not a purchase: if an allocated portion is declined or left unpaid, it is offered to the reserve queue in order. It may never be offered.</p>
      <div class="asp-manage__row asp-manage__row--single">
        <div class="asp-manage__info"><p class="asp-review__name">Reserve portions</p><p class="asp-review__meta">${winner ? `${escapeHtml(winner.priceText)} per portion if offered` : ''}</p></div>
        <div class="asp-manage__control">${renderStepper('reserve', 'the reserve queue', ui.reserveQuantity, bounds, 'reserve', ui.uncertain)}<p class="asp-select__limit">Max ${bounds.max}</p></div>
      </div>
      <ul class="asp-notes" role="list"><li>You join at the back of the reserve queue. Nothing is charged unless portions are offered to you and you pay.</li></ul>`;
    return renderModalFrame(ids, 'Join the reserve queue', body, cancelButton(ui) + primaryButton('Join the reserve queue', 'Joining…', 'join-reserve', ui), ui);
  }

  /** The quantity range for the join/claim dialog: the server's minPortions to its maxPortions (claims: never above what is available). */
  function joinBounds(vm) {
    if (vm.claim) return { min: vm.claim.min, max: vm.claim.max, current: vm.claim.min };
    return { min: 1, max: vm.postActions.joinReserve ? vm.postActions.joinReserve.max : 1, current: 1 };
  }

  function renderWithdrawReserve(vm, ids, ui) {
    const portions = vm.actions.withdrawReserve.portions;
    const body = `
      <p class="asp-dialog__lede">Leaving withdraws all ${escapeHtml(plural(portions, 'reserve portion'))}. If you rejoin later, you’ll join at the back of the queue.</p>`;
    return renderModalFrame(ids, 'Leave the reserve queue?', body, cancelButton(ui) + primaryButton('Leave the reserve queue', 'Leaving…', 'withdraw-reserve', ui), ui);
  }

  function renderDecline(vm, ids, ui) {
    const keep = `<button type="button" class="asp-btn asp-btn--quiet asp-dialog__secondary" data-asp-close${ui.busy ? ' disabled' : ''}>${escapeHtml(DECLINE_COPY.keep)}</button>`;
    return renderModalFrame(ids, DECLINE_COPY.title, renderDeclineDetails(vm.declineSummary), keep + primaryButton(DECLINE_COPY.confirm, DECLINE_COPY.busy, 'decline', ui), ui, DECLINE_ACTIONS_CLASS);
  }

  const MODALS = { review: renderReview, manage: renderManage, 'join-reserve': renderJoinReserve, 'withdraw-reserve': renderWithdrawReserve, decline: renderDecline };

  // ===== Behaviour ==========================================================================

  function prefersReducedMotion(win) {
    return Boolean(win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function initCarousel(root, win) {
    const viewport = root.querySelector('[data-asp-viewport]');
    const nav = root.querySelector('[data-asp-nav]');
    const prev = root.querySelector('[data-asp-prev]');
    const next = root.querySelector('[data-asp-next]');
    if (!viewport || !nav || !prev || !next) return;
    const status = root.querySelector('[data-asp-status]');
    const dots = root.querySelector('[data-asp-dots]');
    const cards = [...viewport.querySelectorAll('.asp-card')];
    const behavior = () => (prefersReducedMotion(win) ? 'instant' : 'smooth');

    const step = () => {
      if (cards.length < 2) return viewport.clientWidth;
      return cards[1].offsetLeft - cards[0].offsetLeft;
    };

    const scrollByCards = (direction) => viewport.scrollBy({ left: direction * step(), behavior: behavior() });
    const scrollToCard = (index) => viewport.scrollTo({ left: cards[index].offsetLeft - cards[0].offsetLeft, behavior: behavior() });

    let lastStatus = '';
    let dotCount = -1;
    const update = () => {
      const overflow = viewport.scrollWidth > viewport.clientWidth + 2;
      nav.hidden = !overflow;
      prev.hidden = !overflow;
      next.hidden = !overflow;
      if (overflow) {
        viewport.setAttribute('tabindex', '0');
      } else {
        viewport.removeAttribute('tabindex');
      }
      const atStart = viewport.scrollLeft <= 2;
      const atEnd = viewport.scrollLeft + viewport.clientWidth >= viewport.scrollWidth - 2;
      prev.setAttribute('aria-disabled', String(atStart));
      next.setAttribute('aria-disabled', String(atEnd));

      const width = step();
      const gap = cards.length > 1 && cards[0].offsetWidth ? width - cards[0].offsetWidth : 0;
      const visible = width > 0 ? Math.max(1, Math.floor((viewport.clientWidth + gap + 1) / width)) : 1;
      const first = atEnd ? Math.max(1, cards.length - visible + 1) : (width > 0 ? Math.min(cards.length, Math.round(viewport.scrollLeft / width) + 1) : 1);
      const last = atEnd ? cards.length : Math.min(cards.length, first + visible - 1);
      const text = overflow ? `${first === last ? first : `${first}–${last}`} of ${cards.length} options` : '';
      if (text !== lastStatus) {
        status.textContent = text;
        lastStatus = text;
      }

      // One dot per scroll position, as in the prototype.
      if (dots) {
        const positions = overflow ? Math.max(1, cards.length - visible + 1) : 0;
        if (positions !== dotCount) {
          dotCount = positions;
          dots.innerHTML = Array.from({ length: positions }, (_, index) =>
            `<button type="button" class="asp-carousel__dot" data-asp-dot="${index}" aria-label="Show options ${index + 1}–${Math.min(cards.length, index + visible)}"><span></span></button>`).join('');
        }
        [...dots.querySelectorAll('[data-asp-dot]')].forEach((dot, index) => dot.setAttribute('aria-current', String(index === first - 1)));
      }
    };

    prev.addEventListener('click', () => { if (prev.getAttribute('aria-disabled') !== 'true') scrollByCards(-1); });
    next.addEventListener('click', () => { if (next.getAttribute('aria-disabled') !== 'true') scrollByCards(1); });
    if (dots) {
      dots.addEventListener('click', (event) => {
        const dot = event.target && event.target.closest ? event.target.closest('[data-asp-dot]') : null;
        if (dot) scrollToCard(Number(dot.getAttribute('data-asp-dot')));
      });
    }
    viewport.addEventListener('keydown', (event) => {
      if (event.target !== viewport) return;
      const keys = { ArrowRight: 1, ArrowLeft: -1 };
      if (keys[event.key]) {
        event.preventDefault();
        scrollByCards(keys[event.key]);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        viewport.scrollTo({ left: event.key === 'Home' ? 0 : viewport.scrollWidth, behavior: behavior() });
      }
    });

    // One update per frame. The flag is set before scheduling, so it also holds if a
    // requestAnimationFrame implementation runs its callback synchronously.
    let pending = false;
    const schedule = () => {
      if (pending) return;
      pending = true;
      win.requestAnimationFrame(() => { pending = false; update(); });
    };
    viewport.addEventListener('scroll', schedule, { passive: true });
    if (typeof win.ResizeObserver === 'function') {
      new win.ResizeObserver(schedule).observe(viewport);
    } else {
      win.addEventListener('resize', schedule);
    }
    update();
  }

  function showModal(dialog) {
    if (!dialog) return;
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
  }

  function closeModal(dialog) {
    if (!dialog) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
  }

  /**
   * The block renders after the whole buy-buttons block, and that block ends with the Archive
   * metadata (share, classification, community reference, awards). Shared Pour is another way to
   * acquire the same bottle, so it belongs directly under the purchase controls: move our own,
   * still-hidden root to just before that metadata in the same column. Nothing else is moved;
   * without the metadata the root stays where it is.
   */
  function placeComponent(root) {
    const parent = root && root.parentElement;
    if (!parent || !parent.children) return;
    const metadata = Array.from(parent.children).find((element) => element.classList && element.classList.contains('archive-metadata'));
    const DOCUMENT_POSITION_FOLLOWING = 4;
    if (metadata && (metadata.compareDocumentPosition(root) & DOCUMENT_POSITION_FOLLOWING)) {
      parent.insertBefore(root, metadata);
    }
  }

  // Shared Pour emails link to /products/{handle}#archive-shared-pour (blocks/archive-shared-pour.liquid).
  const ANCHOR_ID = 'archive-shared-pour';

  /**
   * The browser's own fragment scroll happens while the root is still hidden and empty, so it
   * lands nowhere. Once the component is shown, scroll to it - only for exactly that fragment.
   * Instant, like the browser's own fragment navigation (this is where the page opens, not motion).
   */
  function scrollToAnchor(root, win) {
    let hash = '';
    try { hash = (win && win.location && win.location.hash) || ''; } catch (error) { return false; }
    if (hash !== `#${ANCHOR_ID}` || !root || root.id !== ANCHOR_ID || typeof root.scrollIntoView !== 'function') return false;
    root.scrollIntoView({ block: 'start', behavior: 'instant' });
    return true;
  }

  function readConfig(root) {
    const endpoint = root.getAttribute('data-endpoint');
    if (!endpoint) return null;
    return {
      endpoint,
      apiBase: root.getAttribute('data-api-base') || '/apps/archive/shared-pour',
      loginUrl: root.getAttribute('data-login-url'),
      fallbackImageUrl: root.getAttribute('data-fallback-image'),
      mySharedPoursUrl: root.getAttribute('data-my-shared-pours-url'),
      // Shopify's availability when the page was rendered (blocks/archive-shared-pour.liquid). It
      // only decides whether the banner's purchase line is shown, never an action.
      bottleAvailable: root.getAttribute('data-bottle-available') === 'true'
    };
  }

  const ACTION_PATHS = {
    confirm: '/commitments',
    change: '/commitments/change',
    'join-reserve': '/reserve',
    'withdraw-reserve': '/reserve/withdraw',
    decline: '/allocation/decline'
  };

  const SUCCESS_COPY = {
    confirm: 'Your commitment is confirmed.',
    change: 'Your commitments have been updated.',
    'join-reserve': 'You’ve joined the reserve queue.',
    'withdraw-reserve': 'You’ve left the reserve queue.',
    decline: DECLINE_COPY.success
  };
  const RECOVERY_SUCCESS = 'Your commitment has been received.';
  // The refreshed view shows the allocation (being prepared, then payment due) the claim produced.
  const CLAIM_SUCCESS = 'Your claim has been received. Your allocation is shown below.';

  /** The request body for an action, from the current view and the customer's choices only. */
  function actionPayload(kind, vm, ui) {
    switch (kind) {
      case 'confirm':
        return {
          sharedPourId: vm.sharedPourId,
          items: vm.packages.filter((pkg) => (ui.selection[pkg.id] || 0) > 0).map((pkg) => ({ packageId: pkg.id, quantity: ui.selection[pkg.id] })),
          termsVersion: vm.termsVersion
        };
      case 'change':
        return {
          sharedPourId: vm.sharedPourId,
          items: vm.packages.filter((pkg) => ui.drafts[pkg.id] !== pkg.currentQuantity).map((pkg) => ({ packageId: pkg.id, newTotal: ui.drafts[pkg.id] }))
        };
      case 'join-reserve':
        return { sharedPourId: vm.sharedPourId, quantity: ui.reserveQuantity, termsVersion: vm.termsVersion };
      case 'withdraw-reserve':
        return { sharedPourId: vm.sharedPourId };
      case 'decline':
        return { sharedPourId: vm.sharedPourId, allocationId: vm.actions.decline.allocationId, confirmed: true };
      default:
        return null;
    }
  }

  /**
   * The page-level controller: one root, one initial request. Every later render comes from a
   * server response (the initial view, or the refreshed view an action returns).
   */
  function createController(root, config, win, deps) {
    const base = 'ArchiveSharedPour';
    const ids = { heading: `${base}-heading`, viewport: `${base}-options`, dialog: `${base}-how`, dialogTitle: `${base}-how-title`, actionDialog: `${base}-action`, actionTitle: `${base}-action-title` };
    const intents = createIntents(deps.keyFactory || (() => newIdempotencyKey()));
    const ui = { selection: {}, drafts: {}, reserveQuantity: 1, conflicts: [], notice: null, modal: null, busy: false, uncertain: false, error: null, timeZone: deps.timeZone };
    let response = null;
    let vm = null;
    // Back/forward cache: `generation` changes whenever the page is put away, so nothing requested
    // before (a refresh, an action, a payment link) can be drawn or followed afterwards; `concealed`
    // remembers what was on screen so the same customer, freshly verified, keeps their place.
    let generation = 0;
    let concealed = null;
    let shownHtml = '';

    const view = () => root.querySelector('[data-asp-view]');
    const actionDialog = () => root.querySelector('[data-asp-action-dialog]');

    function setResponse(next) {
      response = next;
      vm = buildViewModel(response, config, { timeZone: deps.timeZone });
      scheduleRecoveryRefresh();
    }

    // When the server's recovery window closes, re-read the view, so a commit button is never left
    // on the page past its window (the server would refuse it anyway: NoCommitmentWindow). Timed
    // from the server's clock; postponed while a request or dialog is in progress.
    let recoveryTimer = null;
    function scheduleRecoveryRefresh() {
      if (!win || typeof win.setTimeout !== 'function') return;
      if (recoveryTimer) win.clearTimeout(recoveryTimer);
      recoveryTimer = null;
      if (!vm || !vm.recovery) return;
      const wait = Date.parse(vm.recovery.closesAtUtc) - Date.parse(response.serverNowUtc) + 2000;
      if (!Number.isFinite(wait) || wait > 0x7fffffff) return;
      const fire = () => {
        recoveryTimer = null;
        if (ui.busy || ui.modal || ui.paying) recoveryTimer = win.setTimeout(fire, 30000);
        else refreshFromProduct();
      };
      recoveryTimer = win.setTimeout(fire, Math.max(0, wait));
    }

    function renderView() {
      const host = view();
      const html = renderComponent(vm, ids, ui);
      // A re-read that brings nothing new (the background refresh on returning from checkout) is
      // not redrawn, so focus, selections and the carousel stay exactly where they were. An emptied
      // view (pagehide, "no longer available") is always drawn afresh.
      if (html === shownHtml && host.innerHTML) return;
      shownHtml = html;
      // A refresh can move a visitor's view to "nothing to show" (e.g. the event closed).
      root.hidden = vm.layout === 'hidden';
      const oldViewport = host.querySelector('[data-asp-viewport]');
      const scrollLeft = oldViewport ? oldViewport.scrollLeft : 0;
      host.innerHTML = html;
      const viewport = host.querySelector('[data-asp-viewport]');
      if (viewport && scrollLeft) viewport.scrollLeft = scrollLeft;
      initCarousel(host, win);
    }

    function renderModal() {
      const dialog = actionDialog();
      const body = dialog && dialog.querySelector('[data-asp-action-body]');
      if (!body || !ui.modal) return;
      body.innerHTML = MODALS[ui.modal](vm, ids, ui);
    }

    function openModal(kind) {
      if (!MODALS[kind] || !vm) return;
      ui.modal = kind;
      ui.error = null;
      if (kind === 'manage') ui.drafts = Object.fromEntries(vm.packages.map((pkg) => [pkg.id, pkg.currentQuantity]));
      if (kind === 'join-reserve') ui.reserveQuantity = joinBounds(vm).min;
      renderModal();
      showModal(actionDialog());
    }

    /** Closes the action dialog and clears its contents, so no stale state is left behind. */
    function clearModal() {
      ui.modal = null;
      closeModal(actionDialog());
      const body = actionDialog() && actionDialog().querySelector('[data-asp-action-body]');
      if (body) body.innerHTML = '';
    }

    /**
     * Closing the dialog abandons the intent. If the last attempt's outcome is unknown, the
     * server state is re-read first, so the customer sees whether it was applied.
     */
    function dismissModal() {
      if (ui.busy) return;
      const wasUncertain = ui.uncertain;
      ui.error = null;
      ui.uncertain = false;
      intents.settle();
      clearModal();
      if (wasUncertain) {
        ui.selection = {};
        ui.drafts = {};
        ui.conflicts = [];
        refreshFromProduct();
      }
    }

    /** Selection changes update only what they affect, so focus and scroll position are kept. */
    function updateSelection(packageId) {
      const pkg = vm.packages.find((p) => p.id === packageId);
      const card = root.querySelector(`[data-asp-card="${packageId}"]`);
      if (!pkg || !card) return;
      // The page now differs from the last full draw, so the next renderView must redraw.
      shownHtml = '';
      const quantity = ui.selection[packageId] || 0;
      const bounds = selectBounds(pkg);
      card.classList.toggle('asp-card--selected', quantity > 0);
      card.classList.remove('asp-card--conflict');
      const output = card.querySelector('[data-asp-qty]');
      if (output) output.textContent = String(quantity);
      const [down, up] = card.querySelectorAll('[data-asp-step]');
      if (down) down.disabled = quantity <= bounds.min;
      if (up) up.disabled = quantity >= bounds.max;
      const panel = root.querySelector('[data-asp-selection]');
      if (panel) {
        const count = selectionCount(vm, ui.selection);
        panel.querySelector('[data-asp-selection-count]').textContent = count.portions ? selectionCountText(count) : 'Nothing chosen yet';
        panel.querySelector('[data-asp-selection-note]').textContent = count.portions ? SELECTION_NOTE : SELECTION_NONE;
        panel.querySelector('[data-asp-open="review"]').disabled = count.portions === 0;
      }
    }

    function step(scope, packageId, direction) {
      // While a request is in flight, or its outcome is unknown, the request cannot change.
      if (ui.busy || ui.uncertain) return;
      if (scope === 'select') {
        const pkg = vm.packages.find((p) => p.id === packageId);
        if (!pkg) return;
        ui.selection[packageId] = stepQuantity(ui.selection[packageId] || 0, direction, selectBounds(pkg));
        ui.conflicts = ui.conflicts.filter((id) => id !== packageId);
        updateSelection(packageId);
      } else if (scope === 'manage') {
        const pkg = vm.packages.find((p) => p.id === packageId);
        if (!pkg) return;
        const bounds = manageBounds(vm, pkg);
        ui.drafts[packageId] = Math.max(bounds.min, Math.min(bounds.max, ui.drafts[packageId] + direction));
        ui.conflicts = ui.conflicts.filter((id) => id !== packageId);
        ui.error = null;
        renderModal();
      } else if (scope === 'reserve' && vm.postActions.joinReserve) {
        const bounds = joinBounds(vm);
        ui.reserveQuantity = Math.max(bounds.min, Math.min(bounds.max, ui.reserveQuantity + direction));
        renderModal();
      }
    }

    async function refreshFromProduct() {
      const asked = generation;
      const fresh = await loadStorefront(productEndpoint(config), { fetchImpl: deps.fetchImpl, timeoutMs: deps.timeoutMs });
      if (asked !== generation) return false; // put away meanwhile: restore() re-reads instead
      if (!fresh) {
        // No longer available to this customer: leave the page as though Shared Pour did not exist.
        closeModal(actionDialog());
        root.hidden = true;
        root.innerHTML = '';
        return false;
      }
      setResponse(fresh);
      renderView();
      return true;
    }

    async function submit(kind) {
      if (ui.busy || !vm) return;
      // After an outcome-unknown failure only the identical request is retried under its key.
      const payload = intents.frozenPayload() || actionPayload(kind, vm, ui);
      if (!payload) return;
      const idempotencyKey = intents.keyFor(kind);
      // Committing to available portions (recovery window), judged on the view the customer acted on.
      const recoveryIntent = kind === 'join-reserve' && Boolean(vm.recovery || vm.claim);
      const claimIntent = kind === 'join-reserve' && Boolean(vm.claim);
      ui.busy = true;
      ui.error = null;
      renderModal();

      const asked = generation;
      const result = await postAction(`${config.apiBase}${ACTION_PATHS[kind]}`, { ...payload, idempotencyKey }, { fetchImpl: deps.fetchImpl, timeoutMs: deps.actionTimeoutMs });
      // Put away meanwhile: the restored page re-reads the server, which knows the outcome.
      if (asked !== generation) return { kind: 'stale' };
      ui.busy = false;

      if (result.kind === 'ok' && isValidResponse({ event: result.body.event })) {
        // The key is consumed: the next intent gets a new one.
        intents.settle();
        ui.uncertain = false;
        setResponse({ serverNowUtc: result.body.serverNowUtc, event: result.body.event, signInRequiredToCommit: false });
        ui.selection = {};
        ui.drafts = {};
        ui.conflicts = [];
        // The refreshed view (allocation being prepared, payment due) says what happened next.
        ui.notice = { tone: 'success', text: claimIntent ? CLAIM_SUCCESS : recoveryIntent ? RECOVERY_SUCCESS : SUCCESS_COPY[kind] };
        clearModal();
        renderView();
        return result;
      }

      if (result.kind === 'ok' || result.kind === 'retry') {
        // Outcome unknown: keep the key and freeze the request, so a retry can never apply twice.
        intents.freeze(payload);
        ui.uncertain = true;
        ui.error = { text: kind === 'decline' ? DECLINE_RETRY_TEXT : ERROR_COPY.TemporarilyUnavailable, retry: true };
        renderModal();
        return result;
      }

      // A definite refusal: nothing was applied and the key was not consumed, so the customer may
      // adjust and resubmit this intent under the same key.
      ui.uncertain = false;
      const body = result.body || {};
      const reasonCode = typeof body.reasonCode === 'string' ? body.reasonCode : null;
      ui.conflicts = reasonCode === 'MultiplePackagesWouldFill' || reasonCode === 'AtPackageCap' || reasonCode === 'CapacityExhausted' || reasonCode === 'ReserveFull'
        ? (Array.isArray(body.affectedPackageIds) ? body.affectedPackageIds.filter((id) => typeof id === 'string') : [])
        : [];
      ui.error = {
        text: (claimIntent && CLAIM_ERROR_COPY[reasonCode]) || (recoveryIntent && RECOVERY_ERROR_COPY[reasonCode]) || errorMessage(reasonCode, body.message || body.error),
        signIn: reasonCode === 'SignInRequired' || result.status === 401 ? vm.loginUrl : null
      };

      // A refused decline is over (already paid, expired, no longer declinable, paused...): close the
      // dialog, say why beside the refreshed panel, and never show the allocation as declined.
      if (kind === 'decline' && !ui.error.signIn) {
        intents.settle();
        ui.error = null;
        ui.notice = declineRefusal(reasonCode, body.message || body.error);
        clearModal();
        if (result.status !== 404 && isObject(body.event) && isValidResponse({ event: body.event })) {
          setResponse({ serverNowUtc: body.serverNowUtc, event: body.event, signInRequiredToCommit: false });
          renderView();
        } else {
          await refreshFromProduct();
        }
        return result;
      }

      if (reasonCode === 'NotFound' || result.status === 404) {
        intents.settle();
        clearModal();
        await refreshFromProduct();
        return result;
      }

      if (reasonCode === 'TermsVersionMismatch') {
        await refreshFromProduct();
      } else if (isObject(body.event) && isValidResponse({ event: body.event })) {
        setResponse({ serverNowUtc: body.serverNowUtc, event: body.event, signInRequiredToCommit: false });
        renderView();
      }

      // If the refreshed state no longer offers this action, the intent is over: explain on the page.
      const stillOffered = kind === 'confirm' ? vm.canSelect
        : kind === 'change' ? vm.canManage
          : kind === 'join-reserve' ? Boolean(vm.postActions.joinReserve)
            : kind === 'withdraw-reserve' ? Boolean(vm.postActions.withdrawReserve)
              : Boolean(vm.postActions.decline);
      if (!stillOffered) {
        intents.settle();
        ui.notice = { tone: 'error', text: ui.error.text };
        ui.error = null;
        clearModal();
        renderView();
        return result;
      }

      if (kind === 'confirm' && ui.conflicts.length) renderView();
      renderModal();
      return result;
    }

    function onClick(event) {
      const target = event.target && event.target.closest ? event.target.closest('button, a') : null;
      if (!target || !root.contains(target)) return;
      if (target.hasAttribute('data-asp-step')) {
        step(target.getAttribute('data-asp-scope'), target.getAttribute('data-package'), Number(target.getAttribute('data-asp-step')));
      } else if (target.hasAttribute('data-asp-open')) {
        openModal(target.getAttribute('data-asp-open'));
      } else if (target.hasAttribute('data-asp-submit')) {
        submit(target.getAttribute('data-asp-submit'));
      } else if (target.hasAttribute('data-asp-pay')) {
        const choice = vm && vm.postActions.pay ? vm.postActions.pay.allocationIds[Number(target.getAttribute('data-asp-pay'))] : null;
        if (choice) payNow(choice);
      } else if (target.hasAttribute('data-asp-pending')) {
        // Placeholder until the My Shared Pours page exists (see renderMySharedPoursAction).
        const note = root.querySelector('[data-asp-pending-note]');
        if (note) {
          note.textContent = 'My Shared Pours is coming soon. Your commitments are shown above.';
          note.hidden = false;
          shownHtml = ''; // changed in place: the next renderView redraws
        }
      } else if (target.hasAttribute('data-asp-open-how')) {
        event.preventDefault();
        const how = root.querySelector('[data-asp-dialog]');
        showModal(how);
        // Every opening starts at the title: a dialog keeps its scroll position between openings
        // (reset once open - a closed dialog is not rendered, so it ignores scrollTop).
        const body = how && how.querySelector('.asp-dialog__inner');
        if (body) body.scrollTop = 0;
        updateScrollCue(how);
      } else if (target.hasAttribute('data-asp-close')) {
        const dialog = target.closest('dialog');
        if (dialog && dialog.hasAttribute('data-asp-action-dialog')) dismissModal();
        else closeModal(dialog);
      }
    }

    /** The shared secure payment flow, for an allocation id the server allows right now. */
    async function payNow(allocationId) {
      if (ui.busy || ui.paying || !vm || !vm.postActions.pay || !vm.postActions.pay.allocationIds.includes(allocationId)) return null;
      ui.paying = allocationId;
      ui.notice = null;
      renderView();
      const asked = generation;
      const result = await requestPayment(config.apiBase, vm.sharedPourId, allocationId, { fetchImpl: deps.fetchImpl, timeoutMs: deps.actionTimeoutMs });
      // Put away meanwhile: never follow (or show) a link that was asked for in the earlier session.
      if (asked !== generation) return { kind: 'stale' };
      if (result.kind === 'redirect') {
        // Straight to Shopify's secure checkout; the button stays busy while the page navigates.
        (deps.navigate || ((url) => win.location.assign(url)))(result.url);
        return { kind: 'redirect' };
      }
      ui.paying = null;
      ui.notice = { tone: 'error', text: result.text };
      if (result.refetch) await refreshFromProduct();
      else renderView();
      return { kind: 'refused', reasonCode: result.reasonCode };
    }

    /**
     * Leaving the page (pagehide - including into the browser's back/forward cache). Everything
     * customer-specific - commitments, allocations, FIFO and reserve positions, notices, an open
     * dialog, unsent choices and the server's answer itself - is removed and the component hidden
     * before the browser keeps its copy, so Back after signing out or switching accounts can never
     * show it, not even for a moment.
     */
    function conceal() {
      generation += 1;
      const host = view();
      if (vm && host && !root.hidden) concealed = { html: host.innerHTML, scrollY: Number(win && win.scrollY) || 0 };
      Object.assign(ui, { selection: {}, drafts: {}, reserveQuantity: 1, conflicts: [], notice: null, modal: null, busy: false, uncertain: false, error: null, paying: null });
      intents.settle();
      root.querySelectorAll('dialog').forEach((dialog) => closeModal(dialog));
      const body = actionDialog() && actionDialog().querySelector('[data-asp-action-body]');
      if (body) body.innerHTML = '';
      if (host) host.innerHTML = '';
      response = null;
      vm = null;
      root.hidden = true;
    }

    /**
     * Restored from the back/forward cache (pageshow with persisted): ask the server again, as
     * whoever is signed in now, and show the component only if it answers - exactly as a fresh page
     * load would (guests and non-testers keep seeing nothing). The same customer's unchanged view
     * keeps its scroll position. An ordinary page load is left alone.
     */
    async function restore(event) {
      if (!event || !event.persisted) return 'ignored';
      const asked = generation;
      const fresh = await loadStorefront(productEndpoint(config), { fetchImpl: deps.fetchImpl, timeoutMs: deps.timeoutMs });
      if (asked !== generation) return 'stale';
      const back = concealed;
      concealed = null;
      if (!fresh) {
        root.hidden = true;
        return 'hidden';
      }
      setResponse(fresh);
      if (!view()) buildFrame(); // an earlier "no longer available" emptied the root
      root.hidden = false;
      renderView();
      if (back && view().innerHTML === back.html && win && typeof win.scrollTo === 'function') win.scrollTo(0, back.scrollY);
      return 'shown';
    }

    function buildFrame() {
      root.innerHTML = `<div class="asp-host" data-asp-view></div>${renderHowItWorks(vm, ids)}
        <dialog class="asp-dialog asp-dialog--action" id="${ids.actionDialog}" data-asp-action-dialog aria-labelledby="${ids.actionTitle}">
          <div class="asp-dialog__inner" data-asp-action-body></div>
        </dialog>`;
      wireDialogs();
    }

    function mount(initial) {
      setResponse(initial);
      buildFrame();
      root.setAttribute('role', 'region');
      root.setAttribute('aria-labelledby', ids.heading);
      renderView();
      root.addEventListener('click', onClick);
      // Back from Shopify's checkout (or another tab): re-read, so a recorded payment shows.
      if (win && win.document && typeof win.document.addEventListener === 'function') {
        win.document.addEventListener('visibilitychange', () => {
          if (win.document.visibilityState === 'visible' && vm && vm.postActions.pay && !ui.busy && !ui.modal) refreshFromProduct();
        });
      }
      // Signing out or switching accounts, then pressing Back: see conceal() and restore().
      if (win && typeof win.addEventListener === 'function') {
        win.addEventListener('pagehide', conceal);
        win.addEventListener('pageshow', restore);
      }
    }

    function wireDialogs() {
      root.querySelectorAll('dialog').forEach((dialog) => {
        // A click on the backdrop (the dialog itself, outside its content) closes it; Escape too.
        dialog.addEventListener('click', (event) => {
          if (event.target !== dialog) return;
          if (dialog.hasAttribute('data-asp-action-dialog')) dismissModal();
          else closeModal(dialog);
        });
        dialog.addEventListener('cancel', (event) => {
          if (dialog.hasAttribute('data-asp-action-dialog')) {
            event.preventDefault();
            dismissModal();
          }
        });
      });
      // How it works: the "Scroll for more" cue follows the reader (see updateScrollCue).
      const how = root.querySelector('[data-asp-dialog]');
      const body = how && how.querySelector('.asp-dialog__inner');
      if (body) body.addEventListener('scroll', () => updateScrollCue(how), { passive: true });
      if (how && win && typeof win.addEventListener === 'function') {
        win.addEventListener('resize', () => { if (how.open) updateScrollCue(how); });
      }
    }

    return { mount, submit, openModal, step, payNow, conceal, restore, state: () => ({ response, vm, ui }) };
  }

  /** Hide and empty the root (and drop the loader's attributes): as though Shared Pour did not exist. */
  function dismiss(root) {
    root.hidden = true;
    root.innerHTML = '';
    if (typeof root.removeAttribute === 'function') {
      root.removeAttribute('data-asp-preload');
      root.removeAttribute('aria-busy');
    }
  }

  /**
   * The page-level entry point: one root, one initial request. The component renders from that
   * response, or the root is hidden and emptied. For tagged testers the block printed the Archive
   * loader in the root (data-asp-preload); it is replaced by the panel, or removed, by the answer.
   */
  async function start(doc, win, deps = {}) {
    const root = doc.querySelector('[data-archive-shared-pour]');
    const config = root ? readConfig(root) : null;
    if (!config) return { rendered: false, reason: 'not-on-page' };
    // A guest preview token, if the page was opened from a preview link. Forwarding it is not
    // authorisation: the backend decides, and a 404 leaves the component hidden as usual.
    config.previewToken = typeof deps.previewToken === 'string' && PREVIEW_TOKEN_PATTERN.test(deps.previewToken) ? deps.previewToken : null;

    // Put away (back/forward cache) before the first answer: that answer belongs to the earlier
    // session and is never drawn. On return the page asks again, as whoever is signed in then.
    let putAway = false;
    const onHide = () => { putAway = true; dismiss(root); };
    const listen = win && typeof win.addEventListener === 'function';
    if (listen) win.addEventListener('pagehide', onHide);

    const outcome = {};
    const response = await loadStorefront(productEndpoint(config), { fetchImpl: deps.fetchImpl, timeoutMs: deps.timeoutMs, outcome });
    // A preview link proves nothing about this visitor's own access, so it never writes a hint.
    const hint = config.previewToken ? null : hintTarget(root, win);
    if (listen && typeof win.removeEventListener === 'function') win.removeEventListener('pagehide', onHide);
    if (putAway) {
      if (listen) {
        const again = (event) => {
          if (!event || !event.persisted) return;
          win.removeEventListener('pageshow', again);
          start(doc, win, deps);
        };
        win.addEventListener('pageshow', again);
      }
      return { rendered: false, reason: 'put-away' };
    }
    // A valid event this visitor is shown nothing for (closed or cancelled, no commitments) is
    // treated like no event: nothing drawn, and no loader next time.
    if (response && buildViewModel(response, config, { timeZone: deps.timeZone }).layout === 'hidden') {
      updateHint(hint, false);
      dismiss(root);
      return { rendered: false, reason: 'not-for-visitor' };
    }
    if (!response) {
      if (outcome.kind === 'none') updateHint(hint, false); // confirmed: nothing here for this visitor
      dismiss(root); // no event, or not for this visitor: the loader (if any) goes, nothing stays
      return { rendered: false, reason: 'no-event' };
    }

    try {
      placeComponent(root);
      const controller = createController(root, config, win, deps);
      root.hidden = false; // Reveal before wiring behaviour: the carousel measures its real width.
      controller.mount(response); // replaces the loader with the panel
      if (typeof root.removeAttribute === 'function') {
        root.removeAttribute('data-asp-preload');
        root.removeAttribute('aria-busy');
      }
      // The Archive settle-in (asp-reveal, shared with My Shared Pours), once the event is confirmed.
      if (root.classList && typeof root.classList.add === 'function') root.classList.add('asp--revealed');
      const anchored = scrollToAnchor(root, win);
      updateHint(hint, true, Date.now(), controller.state().vm.layout === 'banner');
      return { rendered: true, vm: controller.state().vm, controller, anchored };
    } catch (error) {
      // Never leave a half-rendered component (or a loader) behind.
      dismiss(root);
      debug('presentation failed');
      return { rendered: false, reason: 'render-failed' };
    }
  }

  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    // Guard against the script being included twice: still one request per page load.
    if (!window.__archiveSharedPourStarted) {
      window.__archiveSharedPourStarted = true;
      // Taken as soon as the script runs, so the token leaves the address bar before anything else.
      const previewToken = takePreviewToken(window);
      const run = () => {
        start(document, window, { previewToken }).catch(() => debug('unexpected failure'));
      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run, { once: true });
      } else {
        run();
      }
    }
  }

  // The helpers My Shared Pours (assets/archive-my-shared-pours.js) reuses, so both pages share one
  // implementation of formatting, positions, error copy, idempotency and the secure payment flow.
  const LIBRARY = {
    escapeHtml,
    plural,
    formatMoney,
    formatDateTime,
    rangeList,
    positionsPhrase,
    reserveParts,
    safeUrl,
    imageSources,
    errorMessage,
    createIntents,
    newIdempotencyKey,
    postAction,
    requestPayment,
    paymentMessage,
    PAYMENT_BUSY_LABEL,
    PAYMENT_DELAY_NOTE,
    CHECKOUT_NOTE,
    checkoutNote,
    FULFILMENT_LABELS,
    allocationFacts,
    holdsPaidPortions,
    fullyRefunded,
    paymentFigures,
    DECLINE_COPY,
    DECLINE_ACTIONS_CLASS,
    DECLINE_RETRY_TEXT,
    declineRefusal,
    declineSummary,
    renderDeclineDetails
  };

  if (typeof window !== 'undefined' && !window.ArchiveSharedPourLib) {
    window.ArchiveSharedPourLib = Object.freeze(LIBRARY);
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      LIBRARY,
      requestPayment,
      paymentMessage,
      escapeHtml,
      formatMoney,
      formatDateTime,
      formatRanges,
      rangeList,
      positionsPhrase,
      safeUrl,
      imageSources,
      isValidResponse,
      loadStorefront,
      HINT_KEY,
      HINT_TTL_MS,
      youView,
      bannerView,
      memberCount,
      hintTarget,
      updateHint,
      takePreviewToken,
      productEndpoint,
      scrollToAnchor,
      ANCHOR_ID,
      buildViewModel,
      renderComponent,
      renderHowItWorks,
      updateScrollCue,
      renderReview,
      renderManage,
      renderDecline,
      DECLINE_COPY,
      declineSummary,
      declineRefusal,
      renderDeclineDetails,
      selectionCount,
      selectBounds,
      manageBounds,
      stepQuantity,
      errorMessage,
      createIntents,
      newIdempotencyKey,
      postAction,
      actionPayload,
      createController,
      start,
      initCarousel,
      HOW_IT_WORKS,
      INTRO,
      DISCLOSURE
    };
  }
})();
